import {
  type ItineraryListItem,
  type LeadBucket,
  type LeadDetail,
  type LeadFollowUp,
  type LeadStageHistoryEntry,
  effectivePermissionSetHas,
  isLargeLeadQueryByAdults,
  LARGE_LEAD_ADULTS_THRESHOLD,
} from '@utazzo/shared';
import {
  UzAvatar,
  UzBadge,
  UzButton,
  UzCard,
  UzConfirmDialog,
  UzDialog,
  UzDrawer,
  UzDropdownMenu,
  UzSelect,
  UzSpinner,
  UzTabs,
  UzTextarea,
} from '@utazzo/ui';
import {
  ArrowLeft,
  CalendarRange,
  CheckCircle2,
  ChevronDown,
  Clock,
  FileSignature,
  GitBranch,
  ListChecks,
  ListFilter,
  Lock,
  Pencil,
  Phone,
  Plus,
  Power,
  PowerOff,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { missingForActiveLead } from '../active-readiness';
import { isCarryforwardLead } from '../carryforward';
import { CallAnalysisTab } from '../components/CallAnalysisTab';
import { DateTimePicker } from '../components/DateTimePicker';
import { buildCompletePayload } from '../components/FollowUpLogForm';
import { LeadInboxAside } from '../components/LeadInboxAside';
import { LeadMissingFieldsModal } from '../components/LeadMissingFieldsModal';
import { LeadOwnerChanger } from '../components/LeadOwnerChanger';
import { LeadScreenshotsTab } from '../components/LeadScreenshotsTab';
import { LeadStatusChanger } from '../components/LeadStatusChanger';
import { canCloseLeadAsLostDirectly, resolvePendingLeadApproval } from '../lead-approval-access';
import { scopeReasons } from '../reason-scope';
import {
  formatStageHistoryTransition,
  sanitizeStageHistoryNote,
  stageHistoryActorLabel,
} from '../stage-label';
import {
  useQueryStatusOptionsWithBucket,
  useQuerySubStatusOptionsForParent,
} from '../use-lead-options';
import {
  useCallLead,
  useCompleteFollowUp,
  useLead,
  useScheduleFollowUp,
  useTransitionLeadStage,
} from '../use-leads';

import { requestSidebarCollapse } from '@/app/sidebar-collapse';
import { LegacyBadge } from '@/components/LegacyBadge';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useCallStatusesList } from '@/features/call-statuses/use-call-statuses';
import { useCallSubStatusesList } from '@/features/call-sub-statuses/use-call-sub-statuses';
import { LeadContractsTab } from '@/features/contracts/components/LeadContractsTab';
import { useGenerateContract, useLeadContracts } from '@/features/contracts/use-contracts';
import { useFollowupReasonsList } from '@/features/followup-reasons/use-followup-reasons';
import {
  useItinerariesList,
  useCreateItineraryVersion,
  useSetItineraryActive,
} from '@/features/itineraries/use-itineraries';
import { useLostReasonsList } from '@/features/lost-reasons/use-lost-reasons';
import { useMyEffectivePermissions } from '@/features/my-profile/use-my-profile';
import { LeadAmendmentsPanel } from '@/features/ops-cases/components/LeadAmendmentsPanel';
import { useAppConfig } from '@/features/settings/use-settings';
import { openWhatsAppForLead } from '@/features/whatsapp/widget-bus';
import { extractServerError } from '@/lib/api-error';

const KIND_LABEL: Record<LeadFollowUp['kind'], string> = {
  CALL: 'Phone call',
  WHATSAPP: 'WhatsApp',
  EMAIL: 'Email',
  IN_PERSON: 'In person',
  OTHER: 'Other',
};

// Tinted-bg + colored-dot chip for the History table's Call Status column -
// reads as a status indicator at a glance without dominating the row the
// way a solid-fill UzBadge does.
const RESULT_CHIP_CLASS: Record<LeadFollowUp['result'], string> = {
  SCHEDULED: 'bg-blue-50 text-blue-700 ring-blue-200',
  CONNECTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  NO_RESPONSE: 'bg-amber-50 text-amber-700 ring-amber-200',
  DELAYED: 'bg-amber-50 text-amber-700 ring-amber-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  CANCELED: 'bg-zinc-100 text-zinc-600 ring-zinc-200',
};

const RESULT_DOT_CLASS: Record<LeadFollowUp['result'], string> = {
  SCHEDULED: 'bg-blue-500',
  CONNECTED: 'bg-emerald-500',
  NO_RESPONSE: 'bg-amber-500',
  DELAYED: 'bg-amber-500',
  COMPLETED: 'bg-emerald-500',
  CANCELED: 'bg-zinc-400',
};

interface ResultChipProps {
  result: LeadFollowUp['result'];
  callStatus: LeadFollowUp['callStatus'];
  kind: LeadFollowUp['kind'];
}

const ResultChip = ({ result, callStatus, kind }: ResultChipProps): React.JSX.Element => {
  // A CANCELED row is NOT a call outcome - it is a scheduled follow-up that was
  // voided (e.g. the lead was closed/reopened), so no call was ever made. Label
  // it plainly as "Voided" instead of leaking "Canceled - Phone call" into the
  // Call-status column, which reads like a (non-existent) call result.
  const isVoided = result === 'CANCELED';
  // Prefer the master row's call-status name (e.g. "Connected", "Not
  // Connected") because that's the most specific label the operator picked.
  // Fall back to the human-readable result + channel for non-call channels
  // (WhatsApp / Email / In person) which don't carry a call status.
  const label = isVoided
    ? 'Voided'
    : (callStatus?.name ?? `${humanResult(result)} · ${KIND_LABEL[kind]}`);
  const title = isVoided ? 'Follow-up voided - no call was made' : label;
  // Colour by whether the call actually CONNECTED - not by `result`, which is
  // almost always the generic `COMPLETED` (green) and so can't tell Connected
  // from Not Connected. On a call channel we key off the call-status code
  // (connected = green, anything else = amber "not reached"); non-call
  // channels keep the result-based colour.
  const connected = callStatus ? callStatus.code === 'connected' : null;
  const chipClass =
    connected === null
      ? RESULT_CHIP_CLASS[result]
      : connected
        ? RESULT_CHIP_CLASS.CONNECTED
        : RESULT_CHIP_CLASS.NO_RESPONSE;
  const dotClass =
    connected === null
      ? RESULT_DOT_CLASS[result]
      : connected
        ? RESULT_DOT_CLASS.CONNECTED
        : RESULT_DOT_CLASS.NO_RESPONSE;
  return (
    <span
      className={
        'inline-flex max-w-[180px] items-center gap-1.5 rounded-md px-2 py-0.5 text-[11.5px] font-medium ring-1 ring-inset ' +
        chipClass
      }
      title={title}
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`} aria-hidden />
      <span className="truncate">{label}</span>
    </span>
  );
};

export const LeadDetailPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const leadQuery = useLead(id);
  const lead = leadQuery.data ?? null;
  // Below md the 340px aside is hidden, so the lead queue is only reachable
  // through this drawer (desktop layout unchanged).
  const [queueOpen, setQueueOpen] = useState(false);

  // Collapse the sidebar on mount - the two-pane lead detail wants every
  // pixel; the operator can still expand manually after. We don't restore
  // on unmount: operator preference (toggling back via the rail) wins.
  useEffect(() => {
    requestSidebarCollapse(true);
  }, []);

  // Preserve filter context (bucket / status / range / type) when the operator
  // picks a different lead from the aside - so deep-links from pipeline tiles
  // stay sticky.
  const handleSelectLead = (picked: { id: string }): void => {
    const search = params.toString();
    setQueueOpen(false);
    navigate(`/leads/${picked.id}${search ? `?${search}` : ''}`);
  };

  // 2026-07-16. Back to the inbox WITH the shared scope params so the
  // inbox reopens on the same slice the operator was working in - most
  // importantly `ownerId` (the aside's OwnerFilter writes it to the URL
  // live, so an owner picked here round-trips into the inbox picker and
  // vice versa), plus the date range the inbox originally sent us.
  // Aside-internal params (month / carry / lens / tile / f.* / freshToday)
  // stay behind - they mean nothing to the inbox. Tile drills that need a
  // tighter window (e.g. Today's Fresh Leads) use their own flags so they
  // never overwrite `daterange`/`from`/`to` and leave the picker stuck.
  const handleBackToInbox = (): void => {
    const carried = new URLSearchParams();
    for (const key of ['ownerId', 'daterange', 'from', 'to']) {
      const v = params.get(key);
      if (v) carried.set(key, v);
    }
    const search = carried.toString();
    navigate(`/leads${search ? `?${search}` : ''}`);
  };

  return (
    // `overflow-x-hidden` so a wide child (e.g. the history table) can never
    // push a page-level horizontal scrollbar when the nav sidebar is expanded -
    // inner overflow-x-auto containers still scroll themselves.
    <div className="-mx-4 -my-6 flex h-[calc(100vh-3.5rem)] min-h-0 flex-col overflow-x-hidden bg-zinc-50 sm:-mx-6">
      {/* Top breadcrumb bar */}
      <header className="flex flex-wrap items-center gap-3 border-b border-zinc-200 bg-white px-4 py-2.5 sm:px-6">
        <UzButton variant="ghost" size="sm" onClick={handleBackToInbox}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Lead inbox
        </UzButton>
        {/* Mobile/tablet only: opens the lead queue (the aside is hidden
            below md, so this is the only way to switch leads there). */}
        <UzButton
          variant="outline"
          size="sm"
          className="md:hidden"
          onClick={() => setQueueOpen(true)}
        >
          <ListFilter className="mr-1.5 h-4 w-4" />
          Leads
        </UzButton>
        <span className="h-4 w-px bg-zinc-200" />
        <span className="text-sm font-semibold text-zinc-900">Lead detail</span>
        <div className="ml-auto">
          <UzButton size="sm" onClick={() => navigate('/leads/new')}>
            <Plus className="mr-1.5 h-4 w-4" />
            New lead
          </UzButton>
        </div>
      </header>

      {/* Two-pane body */}
      <div className="flex min-h-0 min-w-0 flex-1">
        <div className="hidden w-[280px] shrink-0 md:block">
          <LeadInboxAside selectedLeadId={id ?? null} onSelect={handleSelectLead} />
        </div>

        <main className="min-w-0 flex-1 overflow-y-auto bg-zinc-50">
          {leadQuery.isLoading ? (
            <LoadingState />
          ) : leadQuery.isError || !lead ? (
            <NotFoundState
              onBack={handleBackToInbox}
              forbidden={isForbiddenError(leadQuery.error)}
            />
          ) : (
            <LeadDetailPane lead={lead} />
          )}
        </main>
      </div>

      {/* Mobile/tablet lead queue - same aside, in a left drawer. */}
      <UzDrawer
        open={queueOpen}
        onOpenChange={setQueueOpen}
        side="left"
        title="Leads"
        contentClassName="w-[88vw] max-w-sm p-0"
      >
        <div className="h-full min-h-0 overflow-hidden">
          <LeadInboxAside selectedLeadId={id ?? null} onSelect={handleSelectLead} />
        </div>
      </UzDrawer>
    </div>
  );
};

// ---------- Right pane ----------

interface LeadDetailPaneProps {
  lead: LeadDetail;
}

const LeadDetailPane = ({ lead }: LeadDetailPaneProps): React.JSX.Element => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const me = useCurrentUser();
  const isAgentOrLead = me?.role === 'SALES_AGENT' || me?.role === 'SALES_LEAD';
  // CallerDesk call history: management-only (ADMIN + SALES_LEAD). Agents
  // never see it - even on their own leads - so talk times / recordings stay
  // a review tool, not a self-audit. Same rule enforced server-side by the
  // route's @Roles on `GET /leads/:id/calls`.
  const canSeeCalls = me?.role === 'ADMIN' || me?.role === 'SALES_LEAD';
  // Sales roles attach screenshots on long-haul leads (API enforces the same).
  const canUploadScreenshots =
    me?.role === 'ADMIN' || me?.role === 'SALES_LEAD' || me?.role === 'SALES_AGENT';
  // WhatsApp button follows the ops.whatsapp permission module (same code
  // the widget and the API check).
  const effectivePermissions = useMyEffectivePermissions();
  const canWhatsApp = effectivePermissionSetHas(effectivePermissions, 'ops.whatsapp.read');
  // ADR-0056: contracts moved off hardcoded roles onto `ops.contracts` -
  // the tab follows the read code; the API enforces the same server-side.
  const canSeeContracts = effectivePermissionSetHas(effectivePermissions, 'ops.contracts.read');
  // A Won lead is done for a sales agent / team lead in the sense that there
  // is no forward move left to make - the "New version" / "Create itinerary"
  // action reads as "there's still work to do" when there isn't, so it stays
  // hidden outright for those roles on a Won lead. Admin keeps every
  // control. 2026-08-06: this used to ALSO hide the status changer itself,
  // which meant a non-admin had no way to reopen a Won lead at all (the
  // changer's reopen-request affordance never mounted). The changer is now
  // mounted unconditionally - it collapses to its own read-only /
  // reopen-request view for a closed bucket (LOST or WON) on non-admins -
  // so this flag is scoped to the itinerary action only.
  const wonLeadForNonAdmin = isAgentOrLead && lead.currentBucket === 'WON';
  // A Won / Lost lead is closed - same immutability the follow-up tab already
  // enforces via ClosedLeadBanner. Reopening or amending a closed lead is
  // admin-only, so the Edit button (which would only ever 403 for these roles)
  // is hidden for a sales agent / team lead on EITHER closed bucket. On Lost
  // the status changer itself collapses to a read-only "reopen needs an admin"
  // badge (see LeadStatusChanger); on Won it's hidden entirely.
  const editHiddenForClosedLead =
    isAgentOrLead && (lead.currentBucket === 'LOST' || lead.currentBucket === 'WON');
  const isLargeLeadQuery = isLargeLeadQueryByAdults(lead.travellerAdults);
  const createVersion = useCreateItineraryVersion();
  const callLead = useCallLead();
  const [newVersionDialogOpen, setNewVersionDialogOpen] = useState(false);

  // Total itinerary versions on this lead - powers the count badge on the
  // Itineraries tab label. Same query params as ItinerariesTab below so
  // React Query dedupes to a single fetch; totalItems is authoritative
  // (pagination-aware) whereas rows.length would only reflect the current
  // page.
  const itinerariesCountQuery = useItinerariesList({
    page: 1,
    pageSize: 50,
    sortBy: 'createdAt',
    sortDir: 'desc',
    leadId: lead.id,
    onlyLatestRevisions: false,
  });
  const itineraryRows = itinerariesCountQuery.data?.data ?? [];
  const totalItineraryVersions = itinerariesCountQuery.data?.pagination.totalItems ?? 0;
  // Head of the revision chain - New version clones/extends from this row.
  const latestItinerary = useMemo(() => {
    if (itineraryRows.length === 0) return null;
    const marked = itineraryRows.find((r) => r.isLatestRevision);
    if (marked) return marked;
    return itineraryRows.reduce((best, row) =>
      row.revisionNumber > best.revisionNumber ? row : best,
    );
  }, [itineraryRows]);
  const hasExistingItinerary = totalItineraryVersions > 0 && latestItinerary !== null;

  // Itinerary building only opens once the lead reaches the "Active" status.
  const isActiveStatus =
    lead.currentStatus.code.toLowerCase() === 'active' ||
    lead.currentStatus.name.trim().toLowerCase() === 'active';

  // Same stale filter as LeadStatusChanger — lock Edit / New version while
  // a team-lead approval request is outstanding.
  const awaitingApproval = resolvePendingLeadApproval(
    lead.pendingStatusChangeRequest,
    lead.currentStatus.id,
  );
  const approvalLocksActions = Boolean(awaitingApproval);
  const approvalLockTitle = 'Actions are locked while an approval request is pending.';
  // 2026-08-06. SALES_LEAD approval parity Change B: a pending
  // LOST_TRANSITION does not lock the status changer (see
  // LeadStatusChanger's own `approvalLocksActions`), but it DOES still
  // block editing the lead (above) and logging/scheduling a follow-up
  // (FollowUpTab) - passed down so that tab can hide its form and show
  // the prior lost reason instead. Other pending kinds keep today's
  // behavior in the follow-up tab (form stays usable).
  // 2026-08-06. Shown to EVERY role (incl. admin): a reviewer needs to see the
  // pending Lost reason + the remark (in History below) to approve/reject, so
  // the follow-up form is swapped for the read-only banner for admins too.
  const pendingLost = awaitingApproval?.kind === 'LOST_TRANSITION';

  const handleCreateItinerary = (): void => {
    if (!isActiveStatus || approvalLocksActions) return;
    // Already has a version chain → same blank-vs-copy dialog as the itinerary
    // dashboard "New version" button. First itinerary still opens the blank
    // configurator (no DB write until Generate).
    if (hasExistingItinerary) {
      setNewVersionDialogOpen(true);
      return;
    }
    window.open(`/itineraries/new?leadId=${lead.id}&from=lead`, '_self', 'noopener,noreferrer');
  };

  const startNewVersion = (copyForward: boolean): void => {
    if (!latestItinerary) return;
    createVersion.mutate(
      { id: latestItinerary.id, copyForward },
      {
        onSuccess: (next) => {
          setNewVersionDialogOpen(false);
          navigate(`/itineraries/${next.id}?from=lead`, { state: { freshVersion: true } });
        },
      },
    );
  };

  return (
    // `min-w-0` so this pane respects the parent <main>'s width
    // when a child has implicit min-content larger than the
    // column (long badges, status names without spaces, native
    // <select> intrinsic widths). `overflow-x-hidden` is the
    // belt-and-braces guarantee: even if a single rogue child
    // somewhere refuses to shrink, the pane clips it at the right
    // edge instead of pushing the whole page wider than the
    // viewport. Child scroll containers (e.g. the history table's
    // own `overflow-x-auto`) still work normally - hidden on the
    // pane only suppresses the page-level scrollbar, not the
    // nested ones.
    <div className="flex min-w-0 flex-col overflow-x-hidden">
      {/* Sticky pane header */}
      <div className="sticky top-0 z-10 border-b border-zinc-200 bg-white px-6 py-4">
        <div className="flex flex-wrap items-center gap-3">
          <UzAvatar name={lead.customer.displayName} size="lg" />
          <div className="flex min-w-0 flex-col">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-lg font-semibold text-zinc-900">
                {lead.customer.displayName}
              </span>
              {lead.customer.kind === 'CORPORATE' && <UzBadge tone="default">Corporate</UzBadge>}
              {isLargeLeadQuery && (
                <span
                  className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-800 ring-1 ring-violet-200"
                  title={`Large lead query — more than ${LARGE_LEAD_ADULTS_THRESHOLD} adults travelling`}
                >
                  Large lead query
                </span>
              )}
              <LegacyBadge legacyId={lead.legacyId} />
              {/* Carry-forward pill - same rule (createdAt before this
                month + bucket=OPEN) the list grid + Carry Forward
                strip use, so the operator sees one consistent
                indicator wherever the lead appears. ADR-0042 + the
                2026-06-23 carry-forward refinement. */}
              {isCarryforwardLead(lead) && (
                <span
                  className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200"
                  title="This lead was created in a prior month but is still OPEN - it's a carry-forward."
                >
                  Carry forward
                </span>
              )}
            </div>
            {/* Contact details deliberately NOT shown here (2026-08-10 ops ask) -
              they live in the Customer details card below; the header stays
              reference-only. */}
            <div className="mt-0.5 font-mono text-xs text-zinc-500">{lead.reference}</div>
          </div>
          {/* Primary contact actions sit on the name row, right-aligned. */}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {/* CallerDesk click-to-call: server rings the logged-in agent's
              registered member number first, then bridges the lead, who sees
              the company DID. Outcome is logged manually as a follow-up.
              Plain Call always dials the primary number; when the customer
              also has an alternate number a chevron segment appears and the
              operator can pick which number to bridge. */}
            <div className="flex items-center">
              <UzButton
                size="sm"
                className={lead.customer.altPhone ? 'rounded-r-none' : undefined}
                disabled={!lead.customer.mobileNumber}
                loading={callLead.isPending}
                title={
                  !lead.customer.mobileNumber
                    ? 'No mobile number on file.'
                    : 'Call via CallerDesk: your phone rings first, then the lead is connected.'
                }
                onClick={() =>
                  callLead.mutate({
                    id: lead.id,
                    target: 'PRIMARY',
                    leadName: lead.customer.displayName,
                  })
                }
              >
                <Phone className="mr-1.5 h-4 w-4" />
                Call
              </UzButton>
              {lead.customer.altPhone && (
                <UzDropdownMenu
                  align="end"
                  trigger={
                    <UzButton
                      size="sm"
                      className="rounded-l-none border-l border-white/25 px-1.5"
                      disabled={callLead.isPending}
                      title="Choose which number to call"
                      aria-label="Choose which number to call"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </UzButton>
                  }
                  items={[
                    { type: 'label', label: 'Call which number?' },
                    {
                      label: `Primary · ${lead.customer.mobileNumber ?? '—'}`,
                      disabled: !lead.customer.mobileNumber,
                      onSelect: () =>
                        callLead.mutate({
                          id: lead.id,
                          target: 'PRIMARY',
                          leadName: lead.customer.displayName,
                        }),
                    },
                    {
                      label: `Alternate · ${lead.customer.altPhone}`,
                      onSelect: () =>
                        callLead.mutate({
                          id: lead.id,
                          target: 'ALTERNATE',
                          leadName: lead.customer.displayName,
                        }),
                    },
                  ]}
                />
              )}
            </div>
            {/* WhatsApp: pops the global floating widget straight onto this
              lead's thread (first contact = template; reply = free chat). */}
            {canWhatsApp && (
              <UzButton
                size="sm"
                className="bg-[#25D366] text-white hover:bg-[#1ebe5b]"
                disabled={!lead.customer.mobileNumber}
                title={
                  !lead.customer.mobileNumber
                    ? 'No mobile number on file.'
                    : 'Open the WhatsApp chat for this lead'
                }
                onClick={() =>
                  openWhatsAppForLead({
                    leadId: lead.id,
                    name: lead.customer.displayName,
                    destination:
                      lead.destinationCity?.name ?? extractDestination(lead.title) ?? undefined,
                  })
                }
              >
                <FaWhatsapp className="mr-1.5 h-4 w-4" />
                WhatsApp
              </UzButton>
            )}
          </div>
        </div>
        {/* Secondary actions: their own row under the name, left-aligned. */}
        <div className="mt-3 flex w-full flex-wrap items-center gap-2">
          {!editHiddenForClosedLead && (
            <UzButton
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/leads/${lead.id}/edit`)}
              disabled={approvalLocksActions}
              title={approvalLocksActions ? approvalLockTitle : 'Edit this lead'}
            >
              <Pencil className="mr-1.5 h-4 w-4" />
              Edit
            </UzButton>
          )}
          {/* ADR-0054: inline status changer. Lives BEFORE the Create
              Itinerary button so the operator's primary action - move
              the lead forward - is the leftmost interactive control.
              Does not modify the Edit form. Handles non-LOST direct
              transitions, the not_qualified -> active required-fields
              gate, the LOST/WON approval workflow, and (when admin) the
              approve / reject controls for a pending request.
              2026-08-06: always mounted now, including for a sales
              agent / team lead on a Won lead - LeadStatusChanger itself
              collapses to a reopen-request affordance for a closed
              bucket (LOST or WON) on non-admins, so hiding it here would
              leave them with no reopen path. */}
          <LeadStatusChanger lead={lead} />
          {!wonLeadForNonAdmin && (
            <UzButton
              variant="outline"
              size="sm"
              onClick={handleCreateItinerary}
              disabled={!isActiveStatus || approvalLocksActions}
              loading={createVersion.isPending}
              title={
                approvalLocksActions
                  ? approvalLockTitle
                  : !isActiveStatus
                    ? 'Move the lead to the Active status to create an itinerary.'
                    : hasExistingItinerary
                      ? 'Create a new version of the latest itinerary (blank or copy-forward).'
                      : 'Create a new itinerary seeded from this lead and open the builder.'
              }
            >
              <ListChecks className="mr-1.5 h-4 w-4" />
              {hasExistingItinerary ? 'New version' : 'Create itinerary'}
            </UzButton>
          )}
          {/* ADR-0069: the fixed-departure path. A lead may hold both a FIT and
              an FD itinerary, so this never replaces the button above - it
              opens the template picker and seeds a separate itinerary. */}
          <UzButton
            variant="outline"
            size="sm"
            onClick={() => navigate(`/leads/${lead.id}/fixed-departures`)}
            disabled={!isActiveStatus || approvalLocksActions}
            title={
              approvalLocksActions
                ? approvalLockTitle
                : !isActiveStatus
                  ? 'Move the lead to the Active status to create an itinerary.'
                  : 'Pick an approved fixed-departure template and seed an itinerary from it.'
            }
          >
            <CalendarRange className="mr-1.5 h-4 w-4" />
            Create FD
          </UzButton>
          {/* <UzButton
            size="sm"
            disabled={!lead.customer.mobileNumber && !lead.customer.email}
            title={
              !lead.customer.mobileNumber && !lead.customer.email
                ? 'No phone or email on file.'
                : 'Opens the customer contact in your default app.'
            }
            onClick={() => {
              if (lead.customer.mobileNumber) {
                window.location.href = `https://wa.me/${encodeURIComponent(lead.customer.mobileNumber)}`;
              } else if (lead.customer.email) {
                window.location.href = `mailto:${lead.customer.email}`;
              }
            }}
          >
            <Send className="mr-1.5 h-4 w-4" />
            Contact
          </UzButton> */}
          {/* Who holds the lead, at the right end of this row - directly under
              Call / WhatsApp, its right edge flush with theirs. Plain text until
              the pencil is clicked; then the same reassignment (and the same
              server-side policy) as the Edit form's owner picker. No pencil for
              roles that may not reassign, or on a closed lead. */}
          <LeadOwnerChanger lead={lead} approvalLocked={approvalLocksActions} />
        </div>
      </div>

      {/* Tabs - iteration count sits on the tab-bar row (right), not inside a tab label. */}
      {/* Below md the count badge drops above the strip instead of floating
          over it, and the strip scrolls on its own rather than being clipped. */}
      <div className="px-6 pt-4">
        <div className="relative">
          <div className="pointer-events-none absolute inset-x-0 top-0 z-[1] flex h-10 items-center justify-end max-md:static max-md:mb-2 max-md:h-auto max-md:justify-start">
            <UzBadge
              tone="secondary"
              pill
              className="pointer-events-auto border border-violet-200 bg-violet-100 px-2.5 py-0.5 text-[11px] font-medium text-violet-800 hover:bg-violet-100"
              title="Number of times this lead's detail form was saved after creation"
            >
              Lead iteration count - {lead.detailIterationCount}
            </UzBadge>
          </div>
          {/* Items operations could not book - the seller changes them. */}
          <LeadAmendmentsPanel leadId={lead.id} />
          <UzTabs
            // `?tab=` deep-links a tab (e.g. contracts from the Converted leads screen).
            defaultValue={params.get('tab') ?? 'details'}
            listClassName="max-md:max-w-full max-md:justify-start max-md:overflow-x-auto"
            tabs={[
              { value: 'details', label: 'Lead details', content: <DetailsTab lead={lead} /> },
              {
                value: 'followup',
                label: 'Follow-up',
                // Completed follow-ups only (the History table) - a row that
                // isn't a still-pending SCHEDULED one. Pending follow-ups have
                // their own "Pending follow-up (N)" section and aren't counted.
                count: lead.followUps.filter((f) => f.result !== 'SCHEDULED' || f.completedAt)
                  .length,
                content: <FollowUpTab lead={lead} pendingLost={pendingLost} />,
              },
              {
                value: 'timeline',
                label: 'Timeline',
                content: <TimelineTab lead={lead} />,
              },
              {
                // 2026-06-29. Sits directly after Timeline per ops
                // request. Lists every itinerary revision linked to
                // this lead (all versions, not just the head); the
                // row is a link into `/itineraries/:id` so operators
                // can hop straight into the editor. Kept intentionally
                // sparse - see the ItinerariesTab component below.
                value: 'itineraries',
                label: 'Itineraries',
                count: totalItineraryVersions,
                content: <ItinerariesTab leadId={lead.id} />,
              },
              // Contract e-signing status: auto-sent on first-instalment
              // payment; this tab shows the signing timeline plus
              // resend / download / void levers. See features/contracts.
              // Gated on `ops.contracts.read` (ADR-0056) - grant from /roles.
              ...(canSeeContracts
                ? [
                    {
                      value: 'contracts',
                      label: 'Contracts',
                      content: <LeadContractsTab leadId={lead.id} />,
                    },
                  ]
                : []),
              // 2026-08-21. Call analysis tab: live CallerDesk CDRs enriched
              // with CallSense AI (recorded-call cards + AI logs panel).
              // Visible to ADMIN + SALES_LEAD only (never SALES_AGENT);
              // the API enforces the same rule server-side.
              ...(canSeeCalls
                ? [
                    {
                      value: 'calls',
                      label: 'Call Analysis',
                      content: <CallAnalysisTab leadId={lead.id} />,
                    },
                  ]
                : []),
              // 2026-10-27. Long-haul leads only: the seller's screenshots
              // (flight fares, customer chats, quotes). See LeadScreenshotsTab.
              ...(lead.isLongHaul
                ? [
                    {
                      value: 'screenshots',
                      label: 'Screenshots',
                      content: (
                        <LeadScreenshotsTab leadId={lead.id} canUpload={canUploadScreenshots} />
                      ),
                    },
                  ]
                : []),
              // WhatsApp chat deliberately has NO tab here - it lives in the
              // global floating widget (features/whatsapp/WhatsAppWidget) so
              // agents see every thread + unread counts from any page.
            ]}
          />
        </div>
      </div>

      {/* Same blank-vs-copy-forward choice as itinerary dashboard "New version". */}
      <UzDialog
        open={newVersionDialogOpen}
        onOpenChange={(open) => {
          if (!open) setNewVersionDialogOpen(false);
        }}
        title="Create a new version"
        description="The current version stays accessible, and the new one becomes the latest. Start it blank, or copy the current version's plan, days and pricing forward."
        footer={
          <div className="flex justify-end gap-2">
            <UzButton
              variant="ghost"
              onClick={() => setNewVersionDialogOpen(false)}
              disabled={createVersion.isPending}
            >
              Cancel
            </UzButton>
            <UzButton
              variant="outline"
              onClick={() => startNewVersion(false)}
              loading={createVersion.isPending}
            >
              Start blank
            </UzButton>
            <UzButton onClick={() => startNewVersion(true)} loading={createVersion.isPending}>
              Copy current version
            </UzButton>
          </div>
        }
      >
        <span />
      </UzDialog>
    </div>
  );
};

// ---------- Details tab ----------

/**
 * The "why lost" remark for a lead: prefers the approved lost request's note
 * (server resolves the real follow-up note for follow-up-raised requests) and
 * otherwise the latest completed follow-up note - so it works for both
 * approval-gated and direct-close lost leads. Shared by the Follow-up tab and
 * the lead-status banners.
 */
const deriveLostRemark = (lead: LeadDetail): string | null => {
  const approvedLostNote =
    lead.lastStatusChangeReview?.status === 'APPROVED' &&
    lead.lastStatusChangeReview.kind === 'LOST_TRANSITION' &&
    lead.lastStatusChangeReview.requestorNote?.trim()
      ? lead.lastStatusChangeReview.requestorNote
      : null;
  const latestFollowUpNote =
    [...lead.followUps]
      .filter((f) => f.completedAt && f.notes?.trim())
      .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))[0]?.notes ?? null;
  return approvedLostNote ?? latestFollowUpNote;
};

const DetailsTab = ({ lead }: { lead: LeadDetail }): React.JSX.Element => {
  const me = useCurrentUser();
  const isAdmin = me?.role === 'ADMIN';
  const isLargeLeadQuery = isLargeLeadQueryByAdults(lead.travellerAdults);
  const customerName = parseDisplayName(lead.customer.displayName);
  // Prefer the structured city refs (post-Phase A schema additions); fall
  // back to the legacy title-parsing for ETL'd rows that don't have the
  // FKs populated yet. See ADR-0032 §13.
  const destination = lead.destinationCity?.name ?? extractDestination(lead.title);
  const departure =
    lead.departureCity?.name ?? lead.departureCityText ?? extractDeparture(lead.title);
  const nights = computeNights(lead.expectedTravelStart, lead.expectedTravelEnd);
  const categoryLabel = lead.leadCategory
    ? lead.leadCategory.charAt(0) + lead.leadCategory.slice(1).toLowerCase()
    : (inferCategory(lead.title) ?? '-');
  return (
    // Below md: an explicit minmax(0,1fr) column so one long unbroken note or
    // URL can't widen every card past the screen (they were clipped on the right).
    <div className="grid gap-4 max-md:grid-cols-1 max-md:break-words">
      {/* Lost reason / remark + any pending-lost or rejected-lost review, right
          here on the main Lead details tab so it's visible without switching to
          the Follow-up tab. */}
      <LeadLostReviewBanners lead={lead} />
      <UzCard title="Customer details">
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
          <KV label="First name" value={customerName.first ?? '-'} />
          <KV label="Last name" value={customerName.last ?? '-'} />
          <KV label="Preferred contact" value={lead.customer.mobileNumber ?? '-'} />
          <KV label="Email" value={lead.customer.email ?? '-'} />
          <KV label="Adults" value={lead.travellerAdults} />
          <KV label="Children" value={lead.travellerChildren} />
          <KV label="Infants" value={lead.travellerInfants} />
          <KV
            label="Child ages"
            value={lead.childAges.length > 0 ? lead.childAges.join(', ') : '-'}
          />
          <KV label="Alternate contact" value={lead.customer.altPhone ?? '-'} />
          <KV label="Corporate?" value={lead.customer.kind === 'CORPORATE' ? 'Yes' : 'No'} />
          {lead.customer.kind === 'CORPORATE' && (
            <KV label="Corporate name" value={lead.customer.companyName ?? '-'} />
          )}
          {/* 2026-07-20 (ops): `isRevertedToCustomer` = TRUE means we HAVE
              reverted to the customer. It is set automatically when the
              itinerary is sent (publish / docket-approval writeback) - no
              manual step - and a lead reads "No" while that revert is still
              pending, which is exactly when it shows in the Revert Pending
              tile. */}
          <KV label="Is Reverted to customer?" value={lead.isRevertedToCustomer ? 'Yes' : 'No'} />
          <KV label="Lead category" value={categoryLabel} />
          <KV label="Lead type" value={lead.queryType.name} />
          {isAdmin && <KV label="Source" value={lead.querySource?.name ?? '-'} />}
        </div>
      </UzCard>

      <UzCard title="Travel details">
        {isLargeLeadQuery && (
          <div className="mb-4 rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-900">
            <span className="font-medium">Large Lead Query</span>
            <span className="text-violet-800">
              {' '}
              — This enquiry has more than {LARGE_LEAD_ADULTS_THRESHOLD} adults (
              {lead.travellerAdults} adults). It is tracked in the pipeline Large Lead Query tile
              and filters.
            </span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
          <KV label="Departure city" value={departure ?? '-'} />
          {/* Labels mirror LeadFormPage: both FKs point at the
              Destinations master; `destinationCity` is the operational
              "Destination" field, `destination` is the create-time
              marketing tag (locked after create). */}
          <KV label="Destination" value={destination ?? '-'} />
          <KV label="Marketing Destination" value={lead.destination?.name ?? '-'} />
          <KV label="Travel date" value={formatDate(lead.expectedTravelStart)} />
          <KV label="Return date" value={formatDate(lead.expectedTravelEnd)} />
          <KV label="Nights" value={nights ?? '-'} />
          <KV label="Hotel category" value={formatHotelCategory(lead.preferredHotelCategory)} />
          {/* Master-driven row wins (ADR-0047); fall back to the
              ADR-0035 enum so archived rows still display their
              pre-migration value. */}
          <KV label="Meal plan" value={lead.mealPlan?.name ?? formatMealOption(lead.mealOption)} />
          <KV label="Budget" value={formatBudget(lead.budgetAmount, lead.budgetCurrency)} />
          <KV label="TTV (quoted)" value={formatBudget(lead.ttvAmount, lead.ttvCurrency)} />
          <KV label="Booking timeline" value={formatBookingTimeline(lead.bookingTimeline)} />
          <KV label="Expected payment date" value={formatDate(lead.expectedPaymentDate)} />
        </div>
      </UzCard>

      <UzCard title="Travel intent">
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
          <KV
            label="Lead Intent"
            value={
              lead.priority ? lead.priority.charAt(0) + lead.priority.slice(1).toLowerCase() : '-'
            }
          />
          <KV label="First-time traveller?" value={formatTriState(lead.isFirstTimeTraveler)} />
          <KV label="Honeymoon couple?" value={formatTriState(lead.isHoneymoonCouple)} />
          <KV label="Valid passport?" value={formatTriState(lead.hasValidPassport)} />
          <KV label="Decision maker?" value={formatTriState(lead.isDecisionMaker)} />
          <KV label="Flights booked?" value={formatTriState(lead.isFlightBooked)} />
          <KV label="Preferred travel mode" value={lead.preferredTravelMode || '-'} />
        </div>
      </UzCard>

      <UzCard title="Enquiry context">
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
          <KV label="Reference" value={lead.reference} />
          <KV
            label="Lead Intent"
            value={
              lead.priority ? lead.priority.charAt(0) + lead.priority.slice(1).toLowerCase() : '-'
            }
          />
          <KV label="Assigned agent" value={lead.assignedAgent?.fullName ?? 'Unassigned'} />
          {lead.originalAssignedAgent &&
            lead.originalAssignedAgent.id !== lead.assignedAgent?.id && (
              <KV label="Original agent" value={lead.originalAssignedAgent.fullName} />
            )}
          <KV label="Branch" value={lead.branch?.name ?? '-'} />
          {isAdmin && (
            <>
              <KV label="Source" value={lead.querySource?.name ?? '-'} />
              <KV label="Sub-source" value={lead.querySubSource?.name ?? '-'} />
              <KV label="Medium" value={lead.queryMedium?.name ?? '-'} />
            </>
          )}
          <KV label="Sub-status" value={lead.currentSubStatus?.name ?? '-'} />
          <KV label="Query date" value={formatDate(lead.queryDate)} />
          {isAdmin && <KV label="Query source URL" value={lead.querySourceUrl ?? '-'} />}
          <KV label="Lost reason" value={lead.lostReason?.name ?? '-'} />
          <KV label="Next follow-up" value={formatDateTime(lead.nextFollowUpAt)} />
          <KV label="Stage entered" value={formatDateTime(lead.stageEnteredAt)} />
          <KV
            label="First contacted"
            value={lead.firstContactedAt ? formatDateTime(lead.firstContactedAt) : '-'}
          />
          <KV label="Closed at" value={formatDateTime(lead.closedAt)} />
          <KV label="Created at" value={formatDateTime(lead.createdAt)} />
        </div>
      </UzCard>

      {lead.assignmentHistory && lead.assignmentHistory.length > 0 && (
        <UzCard title="Assignment history">
          <ul className="space-y-2">
            {lead.assignmentHistory.map((h, i) => (
              <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                <span className="w-32 shrink-0 text-xs text-zinc-500 tabular-nums">
                  {formatDateTime(h.at)}
                </span>
                <span className="text-zinc-900">
                  {h.fromAgent?.fullName ?? 'Unassigned'}{' '}
                  <span className="text-zinc-400">&rarr;</span>{' '}
                  {h.toAgent?.fullName ?? 'Unassigned'}
                </span>
                {h.reason && (
                  <span className="text-[11px] tracking-wide text-zinc-400 uppercase">
                    {h.reason.replace(/_/g, ' ')}
                  </span>
                )}
                {h.changedBy && (
                  <span className="ml-auto text-xs text-zinc-500">by {h.changedBy.fullName}</span>
                )}
              </li>
            ))}
          </ul>
        </UzCard>
      )}

      <UzCard title="What they told us">
        {lead.requirementSummary && (
          <div className="mb-4">
            <div className="text-[11px] font-medium tracking-wide text-zinc-500 uppercase">
              Requirement summary
            </div>
            <p className="mt-1 text-sm leading-relaxed whitespace-pre-wrap text-zinc-800">
              {lead.requirementSummary}
            </p>
          </div>
        )}
        {lead.notes ? (
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-zinc-700">{lead.notes}</p>
        ) : !lead.requirementSummary ? (
          <p className="text-sm text-zinc-500">No notes captured for this lead yet.</p>
        ) : null}
      </UzCard>
    </div>
  );
};

interface KVProps {
  label: string;
  value: React.ReactNode;
}
const KV = ({ label, value }: KVProps): React.JSX.Element => (
  <div className="min-w-0">
    <div className="text-[11px] font-medium tracking-wide text-zinc-500 uppercase">{label}</div>
    <div className="mt-1 truncate text-sm font-medium text-zinc-900">{value}</div>
  </div>
);

// ---------- Follow-up tab ----------

interface FollowUpTabProps {
  lead: LeadDetail;
  // 2026-08-06. True only for a pending LOST_TRANSITION - blocks the
  // schedule/complete form and swaps in a read-only "pending lost"
  // panel instead. Other pending kinds pass `false` (unchanged today).
  pendingLost: boolean;
}

// `NEXT_REQUIRED_OPTIONS` was removed 2026-06-25 with the "Is Next
// Follow-Up Required?" dropdown - the picker is now date-driven
// (a non-empty date implies required).

/// Follow-up channel is hardcoded to a phone call (2026-06-10 rev) -
/// every follow-up logged from this tab is a Phone call. Other channels
/// (WhatsApp / Email / In-person) can come back behind a feature flag
/// when the workflow needs them again.
const FIXED_KIND: LeadFollowUp['kind'] = 'CALL';

/**
 * Small banner shown in place of the follow-up form when the lead is
 * WON or LOST. Tells the operator the lead is closed and points them
 * at the history table below. Server enforces the same gate so a
 * direct API call can't slip past either (ADR-0041).
 */
const ClosedLeadBanner = ({
  bucket,
  statusName,
  lostReason,
  lostRemark,
}: {
  bucket: LeadBucket;
  statusName: string;
  lostReason?: string | null;
  lostRemark?: string | null;
}): React.JSX.Element => {
  const tone =
    bucket === 'WON'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : 'border-red-200 bg-red-50 text-red-700';
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${tone}`}>
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0">
        <p className="font-medium">This lead is {statusName}. Follow-ups are read-only.</p>
        {/* For a LOST lead, keep the reason + remark visible right here so
            everyone sees why it was lost without digging into History. */}
        {bucket === 'LOST' && (lostReason || lostRemark?.trim()) && (
          <p className="mt-0.5">
            {lostReason ? (
              <>
                Reason: <span className="font-normal">{lostReason}</span>
              </>
            ) : null}
            {lostRemark?.trim() ? (
              <>
                {lostReason ? '  -  ' : ''}Remark: <span className="font-normal">{lostRemark}</span>
              </>
            ) : null}
          </p>
        )}
        <p className="mt-0.5 text-xs opacity-90">
          The activity history is below. Reopen the lead from the stage picker if you need to log
          more calls.
        </p>
      </div>
    </div>
  );
};

/**
 * 2026-08-06. Shown in place of the follow-up form while a LOST_TRANSITION
 * approval is pending (SALES_LEAD approval parity Change B). Unlike
 * `ClosedLeadBanner` the lead is still OPEN - status changes stay
 * available from the picker above - but editing the lead and logging a
 * follow-up are blocked until the request is resolved (server enforces
 * the same gate on scheduleFollowUp / completeFollowUp / update for
 * non-admins). Surfaces the earlier lost reason so the operator sees why
 * the lead is pending-lost without hunting for it.
 */
const PendingLostBanner = ({
  lostReasonName,
  remark,
}: {
  lostReasonName: string;
  remark?: string | null;
}): React.JSX.Element => {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
      <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0">
        <p className="font-medium">
          Pending Lost approval - reason: <span className="font-normal">{lostReasonName}</span>
        </p>
        {remark?.trim() && (
          <p className="mt-0.5">
            Remark: <span className="font-normal">{remark}</span>
          </p>
        )}
        <p className="mt-0.5 text-xs opacity-90">
          You can change the lead's status above, but cannot edit it or log a follow-up until the
          request is resolved.
        </p>
      </div>
    </div>
  );
};

/**
 * Shown to the lead OWNER (and everyone) after a move-to-Lost request is
 * REJECTED by a TL/admin: the reviewer's rejection remark plus the reason +
 * note that were submitted, so the owner knows why it came back and can act.
 */
const RejectedLostBanner = ({
  review,
}: {
  review: NonNullable<LeadDetail['lastStatusChangeReview']>;
}): React.JSX.Element => {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
      <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 space-y-0.5">
        <p className="font-medium">
          Move to Lost was rejected
          {review.reviewedBy?.fullName ? ` by ${review.reviewedBy.fullName}` : ''}
        </p>
        {review.reviewerNote?.trim() && (
          <p>
            Reviewer&apos;s remark: <span className="font-normal">{review.reviewerNote}</span>
          </p>
        )}
        <p className="text-xs opacity-90">
          You had submitted - reason: {review.lostReason?.name ?? 'Not specified'}
          {review.requestorNote?.trim() ? `, remark: ${review.requestorNote}` : ''}
        </p>
      </div>
    </div>
  );
};

/**
 * Compact "this lead is Lost" summary (reason + remark) for surfaces other than
 * the Follow-up tab - it has no follow-up-specific read-only text, just the why.
 */
const LostSummaryBanner = ({
  reason,
  remark,
}: {
  reason?: string | null;
  remark?: string | null;
}): React.JSX.Element => {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0">
        <p className="font-medium">This lead is Lost.</p>
        <p className="mt-0.5">
          Reason: <span className="font-normal">{reason ?? 'Not specified'}</span>
          {remark?.trim() ? (
            <>
              {'  -  '}Remark: <span className="font-normal">{remark}</span>
            </>
          ) : null}
        </p>
      </div>
    </div>
  );
};

/**
 * The lead-status review banners (pending-lost / rejected-lost / lost summary),
 * rendered wherever the lost reason + remark should be visible - the Follow-up
 * tab already shows its own variants, this puts the same information on the main
 * Lead details tab so a reviewer/owner doesn't have to switch tabs. Renders
 * nothing when the lead has no lost reason, pending, or rejected review.
 */
const LeadLostReviewBanners = ({ lead }: { lead: LeadDetail }): React.JSX.Element | null => {
  const awaitingApproval = resolvePendingLeadApproval(
    lead.pendingStatusChangeRequest,
    lead.currentStatus.id,
  );
  const pendingLost = awaitingApproval?.kind === 'LOST_TRANSITION';
  const rejectedLost =
    !pendingLost &&
    lead.lastStatusChangeReview?.status === 'REJECTED' &&
    lead.lastStatusChangeReview.kind === 'LOST_TRANSITION';
  const isLost = lead.currentBucket === 'LOST';
  if (!pendingLost && !rejectedLost && !isLost) return null;
  return (
    <div className="space-y-3">
      {pendingLost && (
        <PendingLostBanner
          lostReasonName={lead.pendingStatusChangeRequest?.lostReason?.name ?? 'Not specified'}
          remark={lead.pendingStatusChangeRequest?.requestorNote}
        />
      )}
      {rejectedLost && lead.lastStatusChangeReview && (
        <RejectedLostBanner review={lead.lastStatusChangeReview} />
      )}
      {isLost && (
        <LostSummaryBanner reason={lead.lostReason?.name ?? null} remark={deriveLostRemark(lead)} />
      )}
    </div>
  );
};

const FollowUpTab = ({ lead, pendingLost }: FollowUpTabProps): React.JSX.Element => {
  const schedule = useScheduleFollowUp();
  const complete = useCompleteFollowUp();
  const transition = useTransitionLeadStage();
  const travelIntentEnforce = useAppConfig().data?.leads.travelIntentEnforce ?? false;
  // `useAgentOptionsForLeads()` was dropped 2026-06-25 with the
  // hidden Follow-Up By dropdown.
  const me = useCurrentUser();
  // For a LOST lead: the reason + remark shown on the closed banner.
  const lostReasonName = lead.lostReason?.name ?? null;
  const lostRemark = deriveLostRemark(lead);
  // ADR-0069. A lost-reason outcome closes the lead for admins / team
  // leads and raises an approval request for everyone else - the hint
  // under the Log button says which.
  const closesLostDirectly = canCloseLeadAsLostDirectly(me);
  const callStatusesQuery = useCallStatusesList({
    page: 1,
    pageSize: 50,
    sortDir: 'asc',
    sortBy: 'sortOrder',
    isActive: true,
  });
  const callStatuses = callStatusesQuery.data?.data ?? [];

  // Layout (post 2026-06-10): Follow-Up By + Call Status (channel slot
  // dropped, kind is fixed to phone) + Follow-Up Date+Time + Is Next
  // Required + Remarks. Defaults: agent = me; today's date; no call
  // status pick; no next follow-up.
  const [followUpById, setFollowUpById] = useState<string>('');
  // Follow-Up Date is locked to the moment the operator saves - the
  // call just happened, so the timestamp is "now". State holds an
  // initial display value (mount time); the submit handler below
  // re-stamps it with the actual submit instant so a form left open
  // for a while still records the right time.
  const [followUpAt, setFollowUpAt] = useState<string | null>(() => new Date().toISOString());
  // 2026-07-09. Seed the Call Status from the lead's MOST RECENT
  // completed follow-up's callStatus - operators told us the call
  // status is stable per relationship (once a lead is on "Connected /
  // Move to Active" the next call is very likely the same). They can
  // still change it any time. Hydrated lazily so a subsequent
  // reset() clears it back to '' (not the historical value) - matches
  // the operator's mental model of "starting a fresh call".
  // Both parent + child seeds are pulled from the SAME follow-up row
  // so the pair is always consistent (sub-status belongs to its
  // parent). Picking the newest completed row with a callStatus set.
  const lastCompletedWithCallStatus = useMemo(() => {
    return (lead.followUps ?? [])
      .filter((f) => f.completedAt && f.callStatus?.id)
      .sort((a, b) => (a.completedAt! < b.completedAt! ? 1 : -1))[0];
  }, [lead.followUps]);
  const lastCallStatusId = lastCompletedWithCallStatus?.callStatus?.id ?? '';
  const [callStatusId, setCallStatusId] = useState<string>(lastCallStatusId);
  // Whenever the lead's follow-up history updates AND the operator
  // hasn't touched the picker yet (state still matches the previous
  // last-call-status snapshot), roll forward to the newest snapshot.
  // Prevents the initial mount losing the seed if the query resolved
  // AFTER first render, and keeps a fresh operator's picker aligned
  // when they haven't started editing yet.
  const seededCallStatusRef = useRef<string>(lastCallStatusId);
  useEffect(() => {
    if (callStatusId === seededCallStatusRef.current) {
      seededCallStatusRef.current = lastCallStatusId;
      setCallStatusId(lastCallStatusId);
    } else {
      // Operator picked something - stop tracking further seed updates
      // for THIS mount so we never overwrite an in-progress pick.
      seededCallStatusRef.current = callStatusId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCallStatusId]);
  // Sub Status cascade: fetched ONLY when a parent CallStatus is
  // picked. The query is keyed by `callStatusId` so switching
  // parents drops the previous result and re-fetches. Empty parent
  // skips the request entirely (React Query `enabled: false`).
  // 2026-07-09. Seed the Call Sub Status from the SAME follow-up row
  // as the parent seed so the (status, sub-status) pair is always
  // consistent - avoids sending a sub-status that no longer belongs
  // to the picked parent.
  const lastCallSubStatusId = lastCompletedWithCallStatus?.callSubStatus?.id ?? '';
  const [callSubStatusId, setCallSubStatusId] = useState<string>(lastCallSubStatusId);
  const seededCallSubStatusRef = useRef<string>(lastCallSubStatusId);
  useEffect(() => {
    if (callSubStatusId === seededCallSubStatusRef.current) {
      seededCallSubStatusRef.current = lastCallSubStatusId;
      setCallSubStatusId(lastCallSubStatusId);
    } else {
      seededCallSubStatusRef.current = callSubStatusId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCallSubStatusId]);
  const callSubStatusesQuery = useCallSubStatusesList({
    page: 1,
    pageSize: 50,
    sortDir: 'asc',
    sortBy: 'sortOrder',
    isActive: true,
    // When `callStatusId` is empty the request sends an empty
    // string filter that the server treats as "no filter" - we
    // gate enablement on the React Query side via `enabled`.
    callStatusId: callStatusId || undefined,
  });
  // Loading flag visible to the picker. When no parent is picked
  // the picker is disabled with a hint, so no loading state
  // needed.
  const callSubStatuses = callStatusId ? (callSubStatusesQuery.data?.data ?? []) : [];
  const [nextRequired, setNextRequired] = useState<'yes' | 'no'>('no');
  const [nextAtIso, setNextAtIso] = useState<string | null>(null);
  const [remarks, setRemarks] = useState('');
  // Lost reason picker: required only when the selected call status
  // points at a LOST-bucket target. The lost reasons list is fetched
  // unconditionally (cheap master with <50 rows) so the select is
  // populated the instant the user picks a Not-Interested-style
  // call status.
  const [lostReasonId, setLostReasonId] = useState<string>('');
  const lostReasonsQuery = useLostReasonsList({
    page: 1,
    pageSize: 100,
    sortDir: 'asc',
    sortBy: 'sortOrder',
    isActive: true,
  });
  const lostReasons = lostReasonsQuery.data?.data ?? [];
  // 2026-06-25: Followup Reason picker rendered when the picked
  // sub-status has `isRequireFollowUp = true`. Same master shape as
  // Lost Reasons; cached for the session.
  const [followupReasonId, setFollowupReasonId] = useState<string>('');
  // 2026-07-09. When a follow-up with a "Move to Active" sub-status
  // triggers a not_qualified -> active transition, we pre-check
  // active-readiness and, if the lead is missing required fields,
  // open the same modal LeadStatusChanger uses. `pendingConnectedNote`
  // holds the transition note we would have used, so retrying after the
  // operator completes the missing fields lands the same audit note.
  const [showFollowUpMissingModal, setShowFollowUpMissingModal] = useState(false);
  const [pendingConnectedNote, setPendingConnectedNote] = useState<string | null>(null);
  const followupReasonsQuery = useFollowupReasonsList({
    page: 1,
    pageSize: 100,
    sortDir: 'asc',
    sortBy: 'sortOrder',
    isActive: true,
  });
  const followupReasons = followupReasonsQuery.data?.data ?? [];

  /// Derived "this is the call status the operator picked" once -
  /// used everywhere downstream (transition computation, button
  /// label, Lost Reason cascade).
  const selectedCallStatus = callStatuses.find((s) => s.id === callStatusId);

  /// Seeded `active` QueryStatus + `proposal` QuerySubStatus
  /// resolved by stable machine code. Renaming the display label
  /// in Masters never breaks the lookup.
  const queryStatusesQuery = useQueryStatusOptionsWithBucket();
  const activeStatus = queryStatusesQuery.data.find((s) => s.code === 'active') ?? null;
  const proposalParentForLookup = activeStatus?.id ?? null;
  const proposalSubStatusesQuery = useQuerySubStatusOptionsForParent(proposalParentForLookup);
  const proposalSubStatus =
    proposalSubStatusesQuery.data.find((s) => s.code === 'proposal') ?? null;
  const lostStatus = queryStatusesQuery.data.find((s) => s.code === 'lost') ?? null;

  /// Which combinations trigger an auto-transition. The trigger is the
  /// (CallStatus, CallSubStatus) pair, not the parent alone - the same
  /// "Connected" parent covers many sub-outcomes ("Move to Active",
  /// "Call Back Later", "Send Quote"…) and only the explicit "Move to
  /// Active" sub-status should escalate the lead's stage.
  ///
  ///   sub-status `code === 'move_to_active'`  (display name "Move to
  ///   Active" / "Moved to active")
  ///     → Active / Proposal
  ///
  ///   call-status `code === 'not_interested'`  (display name "Not
  ///   Interested")
  ///     → Lost (with the operator-picked Lost Reason)
  ///
  /// Anything else just logs the call - no stage change.
  const selectedCallSubStatus = callSubStatuses.find((s) => s.id === callSubStatusId);
  /// Scoped to the picked sub-status; empty mapping = every active reason.
  const scopedFollowupReasons = useMemo(
    () => scopeReasons(followupReasons, selectedCallSubStatus?.followupReasons),
    [followupReasons, selectedCallSubStatus],
  );
  /// Same for the lost-reason dropdown. Note the `lostReasons.find(...)`
  /// lookup below stays on the FULL list: it resolves an already-chosen id
  /// to its row, which must not depend on the current scoping.
  const scopedLostReasons = useMemo(
    () => scopeReasons(lostReasons, selectedCallSubStatus?.lostReasons),
    [lostReasons, selectedCallSubStatus],
  );
  const selectedCallStatusName = selectedCallStatus?.name?.toLowerCase().trim();
  const selectedCallSubStatusName = selectedCallSubStatus?.name?.toLowerCase().trim();
  const isMoveToActiveOutcome =
    selectedCallSubStatus?.code === 'move_to_active' ||
    selectedCallSubStatusName === 'move to active' ||
    selectedCallSubStatusName === 'moved to active';
  const isNotInterestedOutcome =
    selectedCallStatus?.code === 'not_interested' || selectedCallStatusName === 'not interested';

  /// Will the next "Log follow-up" click cause an auto-transition?
  /// Used by the button-state + the label below it. Each branch
  /// also returns the *reason* it can't transition (master row
  /// missing, reason not picked) so the label can explain.
  const transitionPlan = (() => {
    if (!selectedCallStatus) return { kind: 'none' as const };
    if (isMoveToActiveOutcome) {
      if (!activeStatus) {
        return { kind: 'connected-misconfigured' as const };
      }
      return {
        kind: 'connected' as const,
        toStatusName: activeStatus.name,
        toSubStatusName: proposalSubStatus?.name ?? null,
      };
    }
    // 2026-06-25 bug fix: was `isNotInterestedOutcome` (parent call
    // status code === 'not_interested') - that never fires because
    // `not_interested` is a SUB-STATUS code under `connected`. The
    // flag-driven check on the sub-status row is the correct trigger.
    // Legacy parent-code path kept for back-compat with un-flagged rows.
    if (selectedCallSubStatus?.isLostReason || isNotInterestedOutcome) {
      if (!lostStatus) return { kind: 'lost-misconfigured' as const };
      if (!lostReasonId) return { kind: 'lost-needs-reason' as const };
      const reason = lostReasons.find((r) => r.id === lostReasonId);
      return {
        kind: 'lost' as const,
        toStatusName: lostStatus.name,
        reasonName: reason?.name ?? 'reason',
      };
    }
    return { kind: 'log-only' as const };
  })();
  // Memoised "today" for the two pickers' minDate. Day-level matcher,
  // so any time within today still picks today's date - only previous
  // days are greyed out. Memoised so the calendar primitive doesn't
  // see a new Date instance on every render.
  const minPickableDate = useMemo(() => new Date(), []);

  useEffect(() => {
    if (!me?.id) return;
    setFollowUpById((cur) => cur || me.id);
  }, [me?.id]);

  // Require-followup: operator picks next datetime. Auto-followup: server
  // schedules at 19:00 IST - no manual picker.
  const subStatusIsRequire = selectedCallSubStatus?.isRequireFollowUp ?? false;
  const subStatusIsAuto = selectedCallSubStatus?.isAutoFollowup ?? false;
  const subStatusIsLost = selectedCallSubStatus?.isLostReason ?? false;
  useEffect(() => {
    if (subStatusIsLost) {
      setNextAtIso(null);
      setNextRequired('no');
      setFollowupReasonId('');
      return;
    }
    if (subStatusIsRequire && !nextAtIso) {
      const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
      setNextAtIso(d.toISOString());
      setNextRequired('yes');
    }
    if (!subStatusIsRequire && !subStatusIsAuto) {
      setNextAtIso(null);
      setNextRequired('no');
    }
    if (subStatusIsAuto && selectedCallSubStatus?.defaultFollowupReasonId) {
      setFollowupReasonId(selectedCallSubStatus.defaultFollowupReasonId);
    }
  }, [
    subStatusIsRequire,
    subStatusIsAuto,
    subStatusIsLost,
    nextAtIso,
    selectedCallSubStatus?.defaultFollowupReasonId,
    callSubStatusId,
  ]);

  /**
   * Reset the form after a successful save. Every field clears
   * EXCEPT Call Status + Sub Status + Follow-up Reason - operators
   * told us those stay stable per relationship (the next call to
   * the same lead usually has the same outcome and the same reason),
   * so we persist them across submissions and only clear the
   * per-call scratch fields. The follow-up timestamp is re-stamped
   * to "now" so the next save carries the actual submit instant.
   *
   * 2026-07-17. Follow-up Reason joined the persistent set (operator
   * request: it came back blank after every submit). The
   * sub-status-driven effect above still clears it when a
   * lost-reason sub-status is picked, and still applies the
   * sub-status's default reason for auto-followup outcomes.
   *
   * 2026-07-09. Any transition-fire logic downstream MUST guard on
   * `lead.currentStatus.code` before dispatching - the persistent
   * pickers mean the same "Move to Active" outcome could be
   * selected again on a lead already in Active, which the
   * SALES_AGENT approval rule (isEnteringActive=false) would
   * reject.
   */
  const reset = (): void => {
    setLostReasonId('');
    setNextRequired('no');
    setNextAtIso(null);
    setRemarks('');
    setFollowUpAt(new Date().toISOString());
  };

  /**
   * Unified Log-Follow-Up handler. Replaces the prior three
   * separate buttons / handlers. Sequence:
   *
   *   1. `schedule` - create the follow-up row stamped at the
   *      submit instant.
   *   2. `complete` - mark it complete with the picked call
   *      status, sub status, remarks, and any next-follow-up.
   *   3. `transition` - ONLY when `transitionPlan` resolves to
   *      `connected` or `lost`. Other plans (log-only,
   *      misconfigured-master, lost-needs-reason) skip the
   *      transition entirely - the call is logged but the lead's
   *      stage is left where it was.
   *
   * Each mutation runs in its own server-side transaction; if the
   * stage transition fails (RBAC, etc.) the follow-up has already
   * been committed and the operator can retry the transition from
   * the lead's stage UI.
   */
  const submit = async (): Promise<void> => {
    if (!followUpById) return;
    if (!followUpAt) return;
    if (!callStatusId) return;
    const status = callStatuses.find((s) => s.id === callStatusId);
    if (!status) return;
    // Guard: Not Interested without a Lost Reason picked is a
    // user error - the button is disabled in that state, so this
    // is a defence-in-depth check.
    if (transitionPlan.kind === 'lost-needs-reason') return;
    // Capture trimmed remarks once - reset() at the end clears
    // them, and the audit note below shouldn't fire blank.
    const trimmedRemarks = remarks.trim() ? remarks.trim() : null;
    // 2026-07-09. Suppress the individual "Follow-up scheduled" +
    // "Follow-up logged" toasts when this submit is going to chain a
    // stage transition. The transition's own success toast
    // ("Moved to Active / Proposal") is the single final outcome the
    // operator cares about. Log-only calls (log the call, no
    // transition) still fire the "Follow-up logged" toast so the
    // operator sees a confirmation.
    const followUpIsCompound = transitionPlan.kind === 'connected';
    // Hoisted so the catch below can re-open the missing-fields modal
    // with the same audit note if the transition 400s.
    let noteForTransition: string | null = null;
    try {
      const submittedAt = new Date().toISOString();
      const oldestPending = pendingFollowUps[0] ?? null;
      let followUpIdToComplete: string;

      if (oldestPending) {
        // Complete the earliest pending slot instead of creating a
        // duplicate SCHEDULED row - keeps missed-follow-up denorm
        // honest and clears the lead from the overdue tile.
        followUpIdToComplete = oldestPending.id;
      } else {
        const created = await schedule.mutateAsync({
          id: lead.id,
          payload: {
            scheduledAt: submittedAt,
            kind: FIXED_KIND,
            agentId: followUpById,
            notes: trimmedRemarks,
          },
          suppressSuccessToast: followUpIsCompound,
        });
        const inserted = created.followUps
          .filter((f) => f.agent.id === followUpById && f.kind === FIXED_KIND)
          .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0];
        if (!inserted) {
          reset();
          return;
        }
        followUpIdToComplete = inserted.id;
      }

      await complete.mutateAsync({
        id: lead.id,
        followUpId: followUpIdToComplete,
        payload: buildCompletePayload({
          result: status.isConnected ? 'CONNECTED' : 'NO_RESPONSE',
          callStatusId,
          callSubStatusId: callSubStatusId || null,
          followupReasonId: followupReasonId || null,
          lostReasonId: subStatusIsLost ? lostReasonId || null : null,
          notes: trimmedRemarks ?? '',
          nextFollowUpAt: subStatusIsRequire && nextRequired === 'yes' ? nextAtIso : null,
        }),
        suppressSuccessToast: followUpIsCompound,
      });
      // Optional stage transition. The TransitionStageRequest is
      // server-validated (sub-status belongs to parent, lost
      // reason required for LOST bucket, RBAC) so we don't
      // duplicate those checks here.
      //
      // 2026-07-09. Extra client-side guard: only fire the
      // transition when the lead is NOT already sitting on Active.
      // Because Call Status + Sub Status now persist across submits
      // (see reset()), the operator can log a normal follow-up on an
      // already-Active lead with the same "Move to Active" sub-status
      // still in the picker. Without this guard, transitionStage
      // would 403 for a sales_agent (isEnteringActive=false), which
      // reads as a phantom "approval required" error to the user.
      if (
        transitionPlan.kind === 'connected' &&
        activeStatus &&
        lead.currentStatus.code !== 'active'
      ) {
        noteForTransition = trimmedRemarks
          ? `Connected via "${status.name}" follow-up: ${trimmedRemarks}`
          : `Connected via "${status.name}" follow-up`;
        // 2026-07-09. Pre-check active-readiness against the current
        // lead. If any of the Travel start / end / Budget / etc. fields
        // are missing we can't fire the transition - the server would
        // 400 with `lead_not_active_ready`. Open the same modal the
        // header status picker uses so the operator fixes the gaps in
        // place, and stash the note so the retry after save uses the
        // same audit trail. The follow-up itself is already persisted
        // above; only the stage transition is deferred.
        const missing = missingForActiveLead(lead, {
          includeTravelIntent: travelIntentEnforce,
        });
        if (missing.length > 0) {
          setPendingConnectedNote(noteForTransition);
          setShowFollowUpMissingModal(true);
          // Skip the fallthrough reset() so the operator's picker state
          // stays visible while the modal is open; the modal's onReady
          // handler will call reset() after the deferred transition
          // finishes.
          return;
        }
        await transition.mutateAsync({
          id: lead.id,
          payload: {
            toStatusId: activeStatus.id,
            toSubStatusId: proposalSubStatus?.id ?? null,
            lostReasonId: null,
            note: noteForTransition,
          },
        });
        // 2026-07-20 (ops): `isRevertedToCustomer` now means "we HAVE
        // reverted to the customer" (the done state), so a Move-to-Active
        // outcome must NOT set it - the lead has just entered
        // active/proposal and the revert is exactly what's still pending.
        // The server rule (active + proposal + !reverted + !itinerarySent)
        // puts it in the Revert Pending tile on its own; the flag flips to
        // TRUE automatically when the itinerary is sent.
        // 2026-07-09. The lead just moved to Active / Proposal.
        // Call Status + Sub Status now stay populated across
        // submits per operator request; the re-fire risk that used
        // to justify clearing them is handled by the
        // `lead.currentStatus.code !== 'active'` guard on the outer
        // if - a second Move-to-Active on an already-Active lead
        // simply skips the transition dispatch. See the note in
        // reset() for the full rationale.
      }
      // Lost closes inside `completeFollowUp` when `lostReasonId` is
      // sent with an `isLostReason` sub-status (ADR-0053) - no separate
      // stage transition needed here.
      reset();
    } catch (err) {
      const extracted = extractServerError(err);
      // Missing-fields modal covers two typed 400 shapes:
      //   - lead_not_active_ready         : transition into Active refused
      //   - active_needs_travel_intent    : backlog gate on the follow-up
      //                                     completion itself (lead is
      //                                     already Active but missing the
      //                                     six travel-intent fields).
      // `noteForTransition` is set only when the transition branch above
      // ran; when it's null (lead already Active), we still open the modal
      // and on save re-run submit() to re-do the whole outcome.
      if (
        extracted.code === 'lead_not_active_ready' ||
        extracted.code === 'active_needs_travel_intent'
      ) {
        if (noteForTransition) setPendingConnectedNote(noteForTransition);
        setShowFollowUpMissingModal(true);
        return;
      }
      // toast already fired by each mutation hook for other error shapes
    }
  };

  const pending = schedule.isPending || complete.isPending || transition.isPending;

  const pendingFollowUps = useMemo(
    () =>
      lead.followUps
        .filter((f) => f.result === 'SCHEDULED' && !f.completedAt)
        .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()),
    [lead.followUps],
  );
  const history = lead.followUps.filter((f) => f.result !== 'SCHEDULED' || f.completedAt);
  const overduePendingCount = pendingFollowUps.filter((f) =>
    isFollowUpOverdue(f.scheduledAt),
  ).length;

  return (
    // 2026-06-25: was `max-w-3xl` (48rem) which clipped the right
    // edge when the main sidebar expanded and shrank the pane
    // below 48rem. Now fluid (`w-full min-w-0`) so the followup
    // panel + history table reflow as the pane resizes. The
    // inner `@container` on the followup card body still drives
    // the responsive grid (1 → 2 → 3 columns) based on the
    // card's own width, not the viewport.
    <div className="grid w-full min-w-0 gap-4">
      {/* Closed-lead immutability. Once a lead lands in WON or LOST,
          the operator can only READ the history - no new follow-ups
          can be scheduled, the pending list is hidden (no actionable
          state on a closed lead), and a small banner tells the
          operator why. Server enforces the same rule (ADR-0041
          §closed-lead-immutability) so direct API calls can't slip
          past. */}
      {lead.currentBucket !== 'OPEN' && (
        <ClosedLeadBanner
          bucket={lead.currentBucket}
          statusName={lead.currentStatus.name}
          lostReason={lostReasonName}
          lostRemark={lostRemark}
        />
      )}
      {/* 2026-08-06. Pending LOST_TRANSITION: the lead is still OPEN (the
          bucket only flips once the request is approved) but the
          schedule/complete form is blocked the same way it is for a
          closed lead - swap in a read-only banner with the prior lost
          reason instead. Other pending kinds fall through to the normal
          form below, unchanged. */}
      {lead.currentBucket === 'OPEN' && pendingLost && (
        <PendingLostBanner
          lostReasonName={lead.pendingStatusChangeRequest?.lostReason?.name ?? 'Not specified'}
          remark={lead.pendingStatusChangeRequest?.requestorNote}
        />
      )}
      {/* A rejected move-to-Lost (no fresh request pending): surface the
          reviewer's remark to the owner so they know why it came back. */}
      {!pendingLost &&
        lead.lastStatusChangeReview?.status === 'REJECTED' &&
        lead.lastStatusChangeReview.kind === 'LOST_TRANSITION' && (
          <RejectedLostBanner review={lead.lastStatusChangeReview} />
        )}
      {lead.currentBucket === 'OPEN' && !pendingLost && (
        <UzCard>
          {/* 2026-06-25: `@container` opens a container-query scope
              so the inner rows react to THIS card's width, not the
              viewport. The sidebar collapse changes the card's
              width without changing the viewport, so viewport
              media queries (`sm:`, `md:`) leave the grid stale and
              the cells overflow. Container queries (`@sm:`,
              `@md:`) recompute on every container resize. */}
          <div className="@container space-y-5">
            {pendingFollowUps.length > 0 && (
              <p className="text-sm text-zinc-600">
                {pendingFollowUps.length === 1
                  ? 'Log the call outcome below - Submit completes the pending follow-up shown under this form.'
                  : 'Submit below completes the earliest pending follow-up first. Repeat for each remaining slot.'}
              </p>
            )}
            {/* 2026-06-25 (adaptive row): Call Status + Call Sub
                Status are always shown. A dynamic third dropdown
                appears when the picked outcome / sub-status needs
                one (Lost Reason / Follow-up Reason). The grid
                column count adapts so the cells always fill the
                row evenly - 2 wide when the third slot is hidden,
                3 wide when it shows. `min-w-0` on each cell so
                long option labels shrink to fit. Mobile collapses
                to one column. */}
            {(() => {
              const selectedStatus = callStatuses.find((s) => s.id === callStatusId);
              const isRequireFollowUp = selectedCallSubStatus?.isRequireFollowUp ?? false;
              // 2026-06-25 bug fix: the Lost Reason dropdown is
              // flag-driven by the SUB-STATUS's `isLostReason`
              // column - NOT by the parent call-status code. In the
              // current seed the `not_interested` row is a CHILD of
              // `connected`, so checking `selectedCallStatus.code ===
              // 'not_interested'` never fires. We also keep the
              // legacy parent-code path so any older sub-status row
              // that hasn't been re-flagged still surfaces.
              const showLostReason = subStatusIsLost || isNotInterestedOutcome;
              // 2026-06-25 ops request: Follow-up Reason renders for
              // every call status (regardless of sub-status flags).
              // It's the default third dropdown; Lost Reason replaces
              // it when isLostReason flips. The `*` (required) marker
              // stays flag-driven via `isRequireFollowUp`. The server
              // now accepts a Follow-up Reason on any call status.
              const hasThirdSlot = Boolean(callStatusId);
              // 2026-06-25: container-query breakpoints so the row
              // collapses to a single column when the card itself
              // narrows (e.g. sidebar expand), instead of waiting
              // for the viewport breakpoint.
              //   < 32rem (@sm threshold): all stacked (grid-cols-1)
              //   >= 32rem: 2 columns when no third slot
              //   >= 48rem: 3 columns when the third slot is present
              //     (drops to 2 columns BELOW that so we don't squeeze
              //      three sub-status names into 360px each)
              const gridCols = hasThirdSlot ? '@md:grid-cols-3 @sm:grid-cols-2' : '@sm:grid-cols-2';
              return (
                <div className={`grid grid-cols-1 gap-4 ${gridCols}`}>
                  <div className="min-w-0">
                    <UzSelect
                      label="Call Status"
                      placeholder="Select a call status"
                      options={callStatuses.map((s) => ({ value: s.id, label: s.name }))}
                      // Pass the raw '' state when cleared - NOT
                      // `|| undefined`. Radix Select treats `undefined`
                      // as "uncontrolled" and keeps the last picked
                      // value visible; UzSelect maps `''` to its own
                      // empty sentinel which no SelectItem matches,
                      // so the trigger correctly falls back to the
                      // placeholder. This is what makes the
                      // call-status field actually clear after a
                      // successful Move-to-Active submit (ADR-0041 §8).
                      value={callStatusId}
                      onValueChange={(v) => {
                        setCallStatusId(v);
                        // Switching parents invalidates any
                        // previously-picked sub-status (it belonged
                        // to the OLD parent). Clear it on change so
                        // the form never persists a child that
                        // doesn't belong to its parent.
                        setCallSubStatusId('');
                      }}
                      disabled={pending || callStatusesQuery.isLoading}
                      hint={selectedStatus?.description ?? undefined}
                    />
                  </div>
                  <div className="min-w-0">
                    <UzSelect
                      label="Call Sub Status"
                      placeholder={
                        !callStatusId
                          ? 'Pick a call status first'
                          : callSubStatusesQuery.isLoading
                            ? 'Loading sub-statuses…'
                            : callSubStatuses.length === 0
                              ? 'No sub-statuses for this call status'
                              : 'Select a sub status'
                      }
                      options={callSubStatuses.map((s) => ({ value: s.id, label: s.name }))}
                      value={callSubStatusId}
                      onValueChange={setCallSubStatusId}
                      disabled={
                        pending ||
                        !callStatusId ||
                        callSubStatusesQuery.isLoading ||
                        callSubStatuses.length === 0
                      }
                    />
                  </div>
                  {/* Dynamic third slot - only rendered when needed,
                      otherwise the parent grid drops to 2 columns
                      and the first two cells expand to fill. */}
                  {hasThirdSlot && (
                    <div className="min-w-0">
                      {showLostReason ? (
                        <UzSelect
                          label="Lost Reason"
                          placeholder="Select a reason"
                          options={scopedLostReasons.map((r) => ({ value: r.id, label: r.name }))}
                          value={lostReasonId}
                          onValueChange={setLostReasonId}
                          disabled={
                            pending || lostReasonsQuery.isLoading || scopedLostReasons.length === 0
                          }
                          required
                          hint={
                            lostReasons.length === 0
                              ? 'Add a Lost Reason via Masters → Lost Reasons before marking leads as lost.'
                              : scopedLostReasons.length === 0
                                ? 'No lost reasons mapped to this sub status. Map them via Masters → Call Sub Statuses.'
                                : undefined
                          }
                        />
                      ) : (
                        <UzSelect
                          label="Follow-up Reason"
                          placeholder={
                            followupReasonsQuery.isLoading ? 'Loading reasons…' : 'Select a reason'
                          }
                          options={scopedFollowupReasons.map((r) => ({
                            value: r.id,
                            label: r.name,
                          }))}
                          value={followupReasonId}
                          onValueChange={setFollowupReasonId}
                          disabled={
                            pending ||
                            followupReasonsQuery.isLoading ||
                            scopedFollowupReasons.length === 0
                          }
                          // `*` only when the picked sub-status flags
                          // it. Otherwise it's an optional capture
                          // field surfaced for every call status.
                          required={isRequireFollowUp}
                          hint={
                            followupReasons.length === 0
                              ? 'Add Followup Reasons via Masters → Followup Reasons.'
                              : scopedFollowupReasons.length === 0
                                ? 'No reasons mapped to this sub status. Map them via Masters → Call Sub Statuses.'
                                : isRequireFollowUp
                                  ? 'Required by the picked sub-status.'
                                  : 'Optional - lets you capture why a follow-up was needed.'
                          }
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Row 2 (Follow-Up Date) moved inside Row 1's left
              column above - keeps the row balanced and stops the
              left half from sitting empty. */}

            {/* Row 2 - Next follow-up date + time (require-followup only). */}
            {selectedCallSubStatus?.isRequireFollowUp && !selectedCallSubStatus?.isLostReason && (
              <DateTimePicker
                label="Next follow-up"
                required
                hint="Mandatory - the picked sub-status requires a next follow-up."
                value={nextAtIso}
                onChange={(d) => {
                  setNextAtIso(d);
                  setNextRequired(d ? 'yes' : 'no');
                }}
                disabled={pending}
                minDate={minPickableDate}
              />
            )}

            {/* Row 5 - Remarks. Mandatory when the sub-status marks the
              lead lost - the server rejects a lost completion without
              `notes`, so require it here too for inline validation. */}
            <UzTextarea
              label="Remarks"
              placeholder="What was discussed? Concerns, asks, next step."
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              disabled={pending}
              required={subStatusIsLost}
              hint={subStatusIsLost ? 'Required when marking the lead lost.' : undefined}
            />

            {/* One-click action row: a single button that does
              everything (log the follow-up and, for Connected /
              Not Interested outcomes, transition the lead's stage
              in the same flow). The label above the button tells
              the operator exactly what the click will do so there
              is no ambiguity between "log only" and "log + move
              stage". */}
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-zinc-600" role="status" aria-live="polite">
                {transitionPlan.kind === 'none' && 'Pick a call status to continue.'}
                {transitionPlan.kind === 'log-only' &&
                  'Call will be logged. Lead stage will not change.'}
                {transitionPlan.kind === 'connected' && (
                  <>
                    Call will be logged and the lead will move to{' '}
                    <span className="font-medium text-zinc-800">
                      {transitionPlan.toStatusName}
                      {transitionPlan.toSubStatusName ? ` → ${transitionPlan.toSubStatusName}` : ''}
                    </span>
                    .
                  </>
                )}
                {transitionPlan.kind === 'connected-misconfigured' && (
                  <>
                    Call will be logged. Missing the <code className="font-mono">active</code> query
                    status - add it via Masters → Query Statuses to auto-move the lead.
                  </>
                )}
                {transitionPlan.kind === 'lost-needs-reason' &&
                  'Pick a Lost Reason to mark this lead as Lost.'}
                {transitionPlan.kind === 'lost' &&
                  (closesLostDirectly ? (
                    <>
                      Call will be logged and the lead will be marked{' '}
                      <span className="font-medium text-zinc-800">
                        {transitionPlan.toStatusName} ({transitionPlan.reasonName})
                      </span>
                      .
                    </>
                  ) : (
                    // ADR-0069: the close is deferred to the reviewer inbox.
                    <>
                      Call will be logged and a{' '}
                      <span className="font-medium text-zinc-800">
                        {transitionPlan.toStatusName} approval request ({transitionPlan.reasonName})
                      </span>{' '}
                      will go to your team lead. The lead stays open until they approve.
                    </>
                  ))}
                {transitionPlan.kind === 'lost-misconfigured' && (
                  <>
                    Call will be logged. Missing the <code className="font-mono">lost</code> query
                    status - add it via Masters → Query Statuses to auto-mark leads as lost.
                  </>
                )}
              </p>
              <UzButton
                onClick={submit}
                loading={pending}
                disabled={
                  pending ||
                  !followUpById ||
                  !followUpAt ||
                  !callStatusId ||
                  (subStatusIsRequire &&
                    (!nextAtIso || new Date(nextAtIso).getTime() <= Date.now())) ||
                  transitionPlan.kind === 'lost-needs-reason' ||
                  (subStatusIsRequire && followupReasonId === '') ||
                  (subStatusIsLost && remarks.trim() === '')
                }
                size="sm"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Submit
              </UzButton>
            </div>
          </div>
        </UzCard>
      )}

      {lead.currentBucket === 'OPEN' && pendingFollowUps.length > 0 && (
        <UzCard title={`Pending follow-up${pendingFollowUps.length === 1 ? '' : 's'}`}>
          {overduePendingCount > 0 && (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {overduePendingCount === 1
                ? '1 follow-up is past due. Log the outcome so this lead clears from missed follow-ups.'
                : `${overduePendingCount} follow-ups are past due. Log each outcome to clear them from missed follow-ups.`}
            </div>
          )}
          <ul className="divide-y divide-zinc-100">
            {pendingFollowUps.map((f) => {
              const overdue = isFollowUpOverdue(f.scheduledAt);
              return (
                <li key={f.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <Clock
                        className={`h-3.5 w-3.5 shrink-0 ${overdue ? 'text-amber-600' : 'text-zinc-400'}`}
                      />
                      <span className="font-medium text-zinc-900">
                        {formatDateTime(f.scheduledAt)}
                      </span>
                      <UzBadge tone="secondary" pill>
                        {KIND_LABEL[f.kind]}
                      </UzBadge>
                      {overdue && (
                        <UzBadge tone="warning" pill>
                          Missed
                        </UzBadge>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-zinc-500">
                      Owner: {f.agent.fullName}
                      {f.notes && <> · {f.notes}</>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </UzCard>
      )}

      <UzCard title={`History (${history.length})`}>
        {history.length === 0 ? (
          <p className="py-2 text-sm text-zinc-500">
            No follow-ups logged yet. Pending follow-ups above move here once you log the outcome.
          </p>
        ) : (
          // Columns (2026-07-16 ops request: the scheduled next follow-up leads
          // the row as "Follow-Up"; the logged timestamp moved right as
          // "Created At"): S.No · Follow-Up (next scheduled) · Call Status ·
          // Status After · Remarks · Next? · Created At (logged) · Follow-Up
          // By. Modern table styling:
          //   - smaller refined chip for call status (tinted bg, not solid)
          //   - two-line date stack (date / time) - easier to scan
          //   - tinted check / muted dash for Yes/No
          //   - subtle row-hover, no thick borders, sticky header band
          //   - agent initials avatar + name on the right
          // `max-w-full` so the wrapper never exceeds its
          // card's content area - the `-mx-4` negative margin
          // only visually extends the border line, not the
          // scroll container's effective width. Without this,
          // the 920px-min table can push the parent card and
          // cascade a page-level horizontal scrollbar.
          <div className="-mx-4 -mb-4 max-w-full overflow-x-auto border-t border-zinc-100">
            <table className="w-full min-w-[920px] text-sm">
              <thead className="bg-zinc-50/60">
                <tr className="text-left text-[10.5px] font-semibold tracking-wider text-zinc-500 uppercase">
                  <th className="px-4 py-2.5">#</th>
                  <th className="px-3 py-2.5">Next Follow-Up</th>
                  <th className="px-3 py-2.5">Call Status</th>
                  <th className="px-3 py-2.5">Status After</th>
                  <th className="px-3 py-2.5">Remarks</th>
                  <th className="px-3 py-2.5 text-center">Next?</th>
                  <th className="px-3 py-2.5">Created At</th>
                  <th className="px-3 py-2.5">Follow-Up By</th>
                  <th className="px-3 py-2.5">Source</th>
                </tr>
              </thead>
              <tbody>
                {history.map((f, idx) => {
                  const followUpDate = f.completedAt ?? f.scheduledAt;
                  const stageAt = leadStageAtTime(lead.stageHistory, followUpDate);
                  return (
                    <tr
                      key={f.id}
                      className="border-t border-zinc-100 align-middle transition-colors first:border-t-0 hover:bg-zinc-50/60"
                    >
                      <td className="px-4 py-3 text-xs font-medium text-zinc-400 tabular-nums">
                        {String(idx + 1).padStart(2, '0')}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {f.nextFollowUpAt ? (
                          <div className="flex flex-col">
                            <span className="text-[13px] font-medium text-zinc-900">
                              {formatDate(f.nextFollowUpAt)}
                            </span>
                            <span className="font-mono text-[11px] text-zinc-500">
                              {formatTime(f.nextFollowUpAt)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-zinc-300">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <ResultChip result={f.result} callStatus={f.callStatus} kind={f.kind} />
                      </td>
                      <td className="px-3 py-3 text-[13px] text-zinc-700">
                        {stageAt === '-' ? (
                          <span className="text-zinc-300">-</span>
                        ) : (
                          <span className="line-clamp-2">{stageAt}</span>
                        )}
                      </td>
                      <td className="max-w-[280px] px-3 py-3">
                        {f.notes ? (
                          <span className="line-clamp-2 text-[13px] text-zinc-700" title={f.notes}>
                            {f.notes}
                          </span>
                        ) : (
                          <span className="text-zinc-300">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {f.nextFollowUpAt ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
                            <CheckCircle2 className="h-3 w-3" />
                            Yes
                          </span>
                        ) : (
                          <span className="text-[11px] text-zinc-400">No</span>
                        )}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-[13px] font-medium text-zinc-900">
                            {formatDate(followUpDate)}
                          </span>
                          <span className="font-mono text-[11px] text-zinc-500">
                            {formatTime(followUpDate)}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          <UzAvatar name={f.agent.fullName} size="sm" />
                          <span className="text-[13px] text-zinc-700">{f.agent.fullName}</span>
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {/* Who created the follow-up: the automated cron vs a
                            human agent. A System row the agent never logged a
                            call on is where "missed" cycles come from. */}
                        <span
                          className={
                            'inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ' +
                            (f.source === 'SYSTEM'
                              ? 'bg-violet-50 text-violet-700 ring-violet-200'
                              : 'bg-zinc-50 text-zinc-600 ring-zinc-200')
                          }
                        >
                          {f.source === 'SYSTEM' ? 'System' : 'Agent'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </UzCard>
      {/* 2026-07-09. Shared "Complete the lead before moving to Active"
          modal. Opens when a follow-up with a Move-to-Active call
          sub-status fires the connected transition, but the lead is
          still missing required Active fields (Travel start/end,
          Budget, etc.). Same component the header status picker uses,
          so operators see identical UX regardless of which entry point
          they used to trigger the move. */}
      <LeadMissingFieldsModal
        open={showFollowUpMissingModal}
        onOpenChange={(open) => {
          setShowFollowUpMissingModal(open);
          if (!open) {
            // Dismissed without a save -> drop the pending note. The
            // follow-up itself has already been persisted; the operator
            // can retry the Move-to-Active by re-selecting the same
            // sub-status when they're ready.
            setPendingConnectedNote(null);
          }
        }}
        lead={lead}
        // Match the server's readiness gate: include travel-intent when
        // enforcement is on.
        mode={travelIntentEnforce ? 'active+travelIntent' : 'active'}
        // 2026-07-10. Preseed the inner LeadFormPage's status picker
        // to Active / Proposal so its `stageChanged` diff picks up
        // the intended move and its submitUpdate fires the
        // transition in the same round-trip as the customer + lead
        // PATCH. Without this the inner picker stayed on the lead's
        // current not_qualified status, no transition fired, and the
        // downstream `onReady` transition ran against a fresh
        // not_qualified lead - producing the "resets to not_qualified"
        // symptom.
        targetStatusId={activeStatus?.id ?? null}
        targetSubStatusId={proposalSubStatus?.id ?? null}
        onReady={async (updatedLead) => {
          // Backlog gate case (2026-08-11): the modal opened because the
          // COMPLETION 400'd on active_needs_travel_intent (lead was
          // already Active, so no pendingConnectedNote was captured). The
          // follow-up itself was not persisted, so re-run submit() with the
          // still-intact form values - it will pass the server gate this
          // time because the six travel-intent fields are now filled.
          if (!pendingConnectedNote) {
            setShowFollowUpMissingModal(false);
            void submit();
            return;
          }
          if (!activeStatus) {
            setShowFollowUpMissingModal(false);
            setPendingConnectedNote(null);
            reset();
            return;
          }
          try {
            // 2026-07-10. The inner LeadFormPage's submitUpdate now
            // preseeds its picker via `targetStatusId` and fires the
            // transition itself when the form's stage-diff is non-empty.
            // Skip this outer transition when the fresh `updatedLead`
            // already sits on Active - the inner path handled it, and
            // re-transitioning would just write an extra stage_history
            // row for the same target.
            if (updatedLead.currentStatus.code !== 'active') {
              await transition.mutateAsync({
                id: lead.id,
                payload: {
                  toStatusId: activeStatus.id,
                  toSubStatusId: proposalSubStatus?.id ?? null,
                  lostReasonId: null,
                  note: pendingConnectedNote,
                },
              });
            }
            // 2026-07-20 (ops): no longer flips `isRevertedToCustomer` -
            // see the direct-submit path. The revert is what's PENDING at
            // this point; the flag turns TRUE when the itinerary is sent.
          } catch {
            // Toast + focus already fired by useTransitionLeadStage's
            // onError via extractServerError.
          } finally {
            // 2026-07-09. Match the direct-submit path: keep Call
            // Status + Sub Status populated across submits, only
            // clear the per-call scratch fields via reset().
            setShowFollowUpMissingModal(false);
            setPendingConnectedNote(null);
            reset();
          }
        }}
      />
    </div>
  );
};

const TimelineTab = ({ lead }: { lead: LeadDetail }): React.JSX.Element => {
  const timeline = useMemo(() => {
    type Entry =
      | { kind: 'stage'; at: string; node: LeadStageHistoryEntry }
      | { kind: 'followup'; at: string; node: LeadFollowUp };
    const entries: Entry[] = [];
    for (const h of lead.stageHistory) {
      entries.push({ kind: 'stage', at: h.changedAt, node: h });
    }
    for (const f of lead.followUps) {
      entries.push({ kind: 'followup', at: f.completedAt ?? f.scheduledAt, node: f });
    }
    return entries.sort((a, b) => (a.at < b.at ? 1 : -1));
  }, [lead.followUps, lead.stageHistory]);

  return (
    <UzCard title="Activity timeline">
      {timeline.length === 0 ? (
        <p className="py-2 text-sm text-zinc-500">Nothing has happened on this lead yet.</p>
      ) : (
        <ol className="space-y-4">
          {timeline.map((e) => (
            <li key={`${e.kind}-${e.node.id}`} className="flex gap-3">
              <div className="mt-0.5 shrink-0">
                {e.kind === 'stage' ? (
                  <GitBranch className="h-4 w-4 text-blue-600" />
                ) : e.node.result === 'COMPLETED' || e.node.result === 'CONNECTED' ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : e.node.result === 'CANCELED' ? (
                  <XCircle className="h-4 w-4 text-zinc-400" />
                ) : (
                  <Clock className="h-4 w-4 text-amber-600" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                {e.kind === 'stage' ? (
                  <>
                    <div className="text-sm font-medium text-zinc-900">
                      {formatStageHistoryTransition(e.node)}
                    </div>
                    <div className="mt-0.5 text-xs text-zinc-500">
                      {stageHistoryActorLabel(e.node)} · {formatDateTime(e.node.changedAt)}
                    </div>
                    {(() => {
                      const cleaned = sanitizeStageHistoryNote(e.node.note);
                      return cleaned ? (
                        <p className="mt-1 text-sm text-zinc-700">{cleaned}</p>
                      ) : null;
                    })()}
                  </>
                ) : (
                  <>
                    <div className="text-sm font-medium text-zinc-900">
                      {KIND_LABEL[e.node.kind]} · {humanResult(e.node.result)}
                    </div>
                    <div className="mt-0.5 text-xs text-zinc-500">
                      {e.node.agent.fullName} · {formatDateTime(e.at)}
                    </div>
                    {e.node.notes && <p className="mt-1 text-sm text-zinc-700">{e.node.notes}</p>}
                  </>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </UzCard>
  );
};

// ---------- Itineraries tab ----------
//
// 2026-06-29. Lists every itinerary revision linked to this lead.
// Deliberately compact - one row per revision with:
//   - "vN" version badge
//   - title
//   - status pill
//   - travel window (start -> end)
//   - grand total in the itinerary's currency
//   - "latest" tag on the head-of-chain row
//   - relative "updated" timestamp
// Row is a button -> navigates to `/itineraries/:id` (the editor).
// Uses the existing `useItinerariesList` hook with
// `onlyLatestRevisions: false` so historical versions show too.

// Enum keys mirror `ItineraryStatusSchema` in packages/shared. Older values
// (SENT/SUPERSEDED/CANCELED) don't exist in the current schema and were
// silently falling through to `default` for every row.
const ITIN_STATUS_META: Record<
  string,
  { tone: 'default' | 'secondary' | 'success' | 'destructive'; label: string }
> = {
  DRAFT: { tone: 'secondary', label: 'Draft' },
  IN_PROGRESS: { tone: 'secondary', label: 'In progress' },
  SENT_TO_CUSTOMER: { tone: 'default', label: 'Sent to customer' },
  REVISED: { tone: 'secondary', label: 'Revised' },
  ACCEPTED: { tone: 'success', label: 'Accepted' },
  REJECTED: { tone: 'destructive', label: 'Rejected' },
  CONVERTED_TO_BOOKING: { tone: 'success', label: 'Converted to booking' },
  CANCELLED: { tone: 'secondary', label: 'Cancelled' },
};

const ItinerariesTab = ({ leadId }: { leadId: string }): React.JSX.Element => {
  const navigate = useNavigate();
  const setActive = useSetItineraryActive();
  const [pendingToggle, setPendingToggle] = useState<{
    row: ItineraryListItem;
    active: boolean;
  } | null>(null);
  // 2026-09-23. Accepted itineraries get a "Send contract" action right here
  // so the operator doesn't have to wait for the payment-triggered auto-send.
  // The server has no duplicate guard on the manual endpoint, so we hide the
  // button once a live (non-declined / non-voided) contract exists.
  const contractsQuery = useLeadContracts(leadId);
  const generateContract = useGenerateContract(leadId);
  const [pendingContract, setPendingContract] = useState<ItineraryListItem | null>(null);
  const contractedItineraryIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of contractsQuery.data ?? []) {
      if (c.itineraryId && c.status !== 'DECLINED' && c.status !== 'VOIDED') {
        ids.add(c.itineraryId);
      }
    }
    return ids;
  }, [contractsQuery.data]);
  const query = useItinerariesList({
    page: 1,
    pageSize: 50,
    sortBy: 'createdAt',
    sortDir: 'desc',
    leadId,
    // Include historical revisions so "all versions" is honest.
    onlyLatestRevisions: false,
  });
  // 2026-06-29. "Latest on top" needs a compound sort - the server
  // can only order by one column so we guarantee the operator-facing
  // rule here:
  //   1. isLatestRevision desc (rows carrying the "Latest" chip
  //      always float to the top of the list, one per chain).
  //   2. revisionNumber desc (within a chain, v3 -> v2 -> v1).
  //   3. createdAt desc (fallback for equal keys - happens on ETL'd
  //      legacy leads where every row is v1 + latest=TRUE, so
  //      chronological order is the meaningful signal).
  const rows = useMemo(() => {
    const data = query.data?.data ?? [];
    return [...data].sort((a, b) => {
      if (a.isLatestRevision !== b.isLatestRevision) return a.isLatestRevision ? -1 : 1;
      if (a.revisionNumber !== b.revisionNumber) return b.revisionNumber - a.revisionNumber;
      return a.createdAt < b.createdAt ? 1 : -1;
    });
  }, [query.data]);

  if (query.isLoading) {
    return (
      <UzCard title="Itineraries">
        <div className="flex items-center gap-2 py-4 text-sm text-zinc-500">
          <UzSpinner size="sm" /> Loading itineraries…
        </div>
      </UzCard>
    );
  }

  if (rows.length === 0) {
    return (
      <UzCard title="Itineraries">
        <p className="py-2 text-sm text-zinc-500">
          No itineraries yet. Create one from the "New itinerary" button up top and it will appear
          here.
        </p>
      </UzCard>
    );
  }

  // Two behaviours on click:
  //   - `?from=lead` so the itinerary editor's back button routes back to the
  //     lead detail page instead of the global itineraries list.
  //   - `state.freshVersion: true` so the "auto-redirect to latest" effect in
  //     ItineraryDetailPage skips - clicking v3 lands on v3, not v4.
  return (
    <>
      <UzCard title={`Itineraries (${rows.length})`}>
        <ul className="divide-y divide-zinc-100">
          {rows.map((it) => (
            <ItineraryRow
              key={it.id}
              it={it}
              onOpen={() =>
                navigate(`/itineraries/${it.id}?from=lead`, {
                  state: { freshVersion: true },
                })
              }
              onToggleActive={(active) => setPendingToggle({ row: it, active })}
              hasContract={contractedItineraryIds.has(it.id)}
              sendingContract={generateContract.isPending && pendingContract?.id === it.id}
              onSendContract={() => setPendingContract(it)}
            />
          ))}
        </ul>
      </UzCard>
      <UzConfirmDialog
        open={pendingToggle !== null}
        onOpenChange={(open) => {
          if (!open) setPendingToggle(null);
        }}
        title={
          pendingToggle
            ? pendingToggle.active
              ? 'Reactivate this itinerary?'
              : 'Deactivate this itinerary?'
            : ''
        }
        description={
          pendingToggle
            ? pendingToggle.active
              ? `${pendingToggle.row.title} will move back to Sent to customer. The share URL will resolve again and the PayU link (if there was one) will be restored.`
              : `${pendingToggle.row.title} will move to Cancelled. The customer share URL will start returning 404, the PayU payment link goes dead, and the row drops out of the version-picker dropdown. You can reactivate any time.`
            : ''
        }
        confirmLabel={pendingToggle?.active ? 'Reactivate' : 'Deactivate'}
        tone={pendingToggle?.active ? 'primary' : 'destructive'}
        onConfirm={async () => {
          if (!pendingToggle) return;
          await setActive.mutateAsync({
            id: pendingToggle.row.id,
            active: pendingToggle.active,
          });
          setPendingToggle(null);
        }}
      />
      <UzConfirmDialog
        open={pendingContract !== null}
        onOpenChange={(open) => {
          if (!open) setPendingContract(null);
        }}
        title="Send the contract for signature?"
        description={
          pendingContract
            ? `The holiday booking contract is generated from ${pendingContract.title} and emailed to the customer for e-signature, followed by the Utazzo countersignature. Track it on the Contracts tab.`
            : ''
        }
        confirmLabel="Generate and send"
        tone="primary"
        onConfirm={async () => {
          if (!pendingContract) return;
          await generateContract.mutateAsync(pendingContract.id);
          setPendingContract(null);
        }}
      />
    </>
  );
};

const ItineraryRow = ({
  it,
  onOpen,
  onToggleActive,
  hasContract,
  sendingContract,
  onSendContract,
}: {
  it: ItineraryListItem;
  onOpen: () => void;
  /** Fires when the operator clicks the activate/deactivate icon. `active`
   *  is the target state (`true` to reactivate a CANCELLED row, `false` to
   *  deactivate a live one). */
  onToggleActive: (active: boolean) => void;
  /** A live contract already exists for this itinerary - show the state
   *  instead of offering to send a duplicate. */
  hasContract: boolean;
  sendingContract: boolean;
  onSendContract: () => void;
}): React.JSX.Element => {
  const window = [it.travelStartDate, it.travelEndDate]
    .filter((v): v is string => Boolean(v))
    .join(' -> ');
  const total = formatItineraryTotal(it.grandTotalPaise, it.currencyCode);
  const meta = ITIN_STATUS_META[it.status] ?? { tone: 'default' as const, label: it.status };
  // Both directions hidden on WON leads: deactivate would pull the live
  // share URL out from under the customer, activate would resurrect a
  // stale sibling. Server enforces the same rules.
  const isCancelled = it.status === 'CANCELLED';
  const canDeactivate =
    !it.leadIsWon &&
    (it.status === 'SENT_TO_CUSTOMER' || it.status === 'ACCEPTED' || it.status === 'REVISED');
  const canActivate = isCancelled && !it.leadIsWon;
  const canToggle = canActivate || canDeactivate;
  // Contract sending only makes sense once the customer has accepted the
  // quote - that's the version the booking contract is built from.
  const canSendContract = it.status === 'ACCEPTED';
  return (
    <li className="flex items-start gap-1">
      <button
        type="button"
        onClick={onOpen}
        className="flex flex-1 cursor-pointer items-start gap-3 py-3 text-left hover:bg-zinc-50"
      >
        <div className="mt-0.5 flex flex-col items-center gap-0.5">
          <span className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-zinc-700">
            v{it.revisionNumber}
          </span>
          {it.isLatestRevision && (
            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium tracking-wider text-emerald-700 uppercase">
              Latest
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="truncate text-sm font-medium text-zinc-900">{it.title}</span>
            <UzBadge tone={meta.tone} pill>
              {meta.label}
            </UzBadge>
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-zinc-500">
            {window && <span>{window}</span>}
            {it.numberOfNights > 0 && (
              <span>
                {it.numberOfNights}N / {it.numberOfDays}D
              </span>
            )}
            {total && <span>{total}</span>}
            <span>Updated {formatRelativeTimeShort(it.updatedAt)}</span>
          </div>
        </div>
      </button>
      {canSendContract &&
        (hasContract ? (
          <span className="mt-3 inline-flex shrink-0 items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Contract sent
          </span>
        ) : (
          <UzButton
            size="sm"
            variant="outline"
            className="mt-2 shrink-0"
            loading={sendingContract}
            onClick={(e) => {
              e.stopPropagation();
              onSendContract();
            }}
          >
            <FileSignature className="mr-1.5 h-3.5 w-3.5" /> Send contract
          </UzButton>
        ))}
      {canToggle && (
        <button
          type="button"
          title={isCancelled ? 'Activate itinerary' : 'Deactivate itinerary'}
          aria-label={isCancelled ? 'Activate itinerary' : 'Deactivate itinerary'}
          onClick={(e) => {
            e.stopPropagation();
            onToggleActive(isCancelled);
          }}
          className={
            isCancelled
              ? 'mt-3 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-emerald-600 transition-colors hover:bg-emerald-50 focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:outline-none'
              : 'mt-3 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-amber-600 transition-colors hover:bg-amber-50 focus-visible:ring-2 focus-visible:ring-amber-200 focus-visible:outline-none'
          }
        >
          {isCancelled ? (
            <Power className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <PowerOff className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
      )}
    </li>
  );
};

/** Format a paise big-int string into the itinerary's currency (or plain rupees). */
function formatItineraryTotal(paise: string, currency: string | null): string | null {
  const n = Number(paise);
  if (!Number.isFinite(n)) return null;
  const rupees = n / 100;
  const code = currency ?? 'INR';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(rupees);
  } catch {
    return `${rupees.toLocaleString('en-IN')} ${code}`;
  }
}

function formatRelativeTimeShort(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  if (!Number.isFinite(then)) return '-';
  const diffMs = now - then;
  const day = 86_400_000;
  if (diffMs < day) return 'today';
  if (diffMs < 2 * day) return 'yesterday';
  if (diffMs < 30 * day) return `${Math.floor(diffMs / day)}d ago`;
  if (diffMs < 365 * day) return `${Math.floor(diffMs / (30 * day))}mo ago`;
  return `${Math.floor(diffMs / (365 * day))}y ago`;
}

// ---------- States ----------

const LoadingState = (): React.JSX.Element => (
  <div className="flex items-center justify-center gap-2 p-12 text-sm text-zinc-500">
    <UzSpinner size="sm" /> Loading lead detail…
  </div>
);

/**
 * Server 403 ("You do not have access to this lead") vs 404/other. The
 * api-client interceptor rejects with the server's error ENVELOPE
 * (`{statusCode, message, ...}`), not the axios error - so check
 * `statusCode` first and keep `response.status` as a fallback for errors
 * that bypass the interceptor.
 */
function isForbiddenError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const e = err as { statusCode?: number; response?: { status?: number } };
  return e.statusCode === 403 || e.response?.status === 403;
}

const NotFoundState = ({
  onBack,
  forbidden = false,
}: {
  onBack: () => void;
  /** 403 from the server - the lead exists but belongs to someone else. */
  forbidden?: boolean;
}): React.JSX.Element => (
  <div className="m-6 rounded-lg border border-zinc-200 bg-white p-8 text-center">
    {forbidden ? (
      <>
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
          <Lock className="h-6 w-6 text-amber-500" />
        </div>
        <p className="text-[15px] font-semibold text-zinc-900">
          This lead isn&apos;t assigned to you
        </p>
        <p className="mx-auto mt-1 max-w-md text-sm text-zinc-500">
          It belongs to another team member, so its details are hidden. If you need access, ask your
          sales lead or an admin to reassign it to you.
        </p>
      </>
    ) : (
      <p className="text-zinc-600">
        We couldn&apos;t load this lead. It may have been deleted, or the URL is wrong.
      </p>
    )}
    <div className="mt-4 flex justify-center">
      <UzButton variant="outline" onClick={onBack}>
        <ArrowLeft className="mr-1.5 h-4 w-4" />
        Back to lead inbox
      </UzButton>
    </div>
  </div>
);

// ---------- Helpers ----------

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** True when the scheduled instant falls before today's local midnight. */
function isFollowUpOverdue(scheduledAt: string): boolean {
  const target = new Date(scheduledAt);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDay = new Date(target);
  targetDay.setHours(0, 0, 0, 0);
  return targetDay.getTime() < today.getTime();
}

/** Date-only (e.g. "04 Feb") for the two-line history table cell. */
function formatTime(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatBudget(
  amount: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amount === null || amount === undefined) return '-';
  const code = currency ?? 'INR';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${code} ${amount.toLocaleString('en-IN')}`;
  }
}

/// ADR-0035 tri-state booleans: null/undefined = unknown.
function formatTriState(v: boolean | null | undefined): string {
  if (v === null || v === undefined) return '-';
  return v ? 'Yes' : 'No';
}

/// Hotel category stored as 1..5 stars or null.
function formatHotelCategory(v: number | null | undefined): string {
  if (v === null || v === undefined) return '-';
  return `${v} star${v === 1 ? '' : 's'}`;
}

/// Booking timeline enum to human label.
function formatBookingTimeline(v: string | null | undefined): string {
  if (!v) return '-';
  const map: Record<string, string> = {
    WITHIN_7_DAYS: 'Within 7 days',
    EIGHT_TO_15_DAYS: '8 - 15 days',
    SIXTEEN_TO_30_DAYS: '16 - 30 days',
    ONE_TO_THREE_MONTHS: '1 - 3 months',
    MORE_THAN_THREE_MONTHS: 'More than 3 months',
  };
  return map[v] ?? v;
}

/// Meal option enum to human label (legacy plan codes per ADR-0035).
function formatMealOption(v: string | null | undefined): string {
  if (!v) return '-';
  const map: Record<string, string> = {
    CPAI: 'CPAI (Continental, all-inclusive)',
    AP: 'AP (All meals)',
    MAP: 'MAP (Breakfast + dinner)',
    CP: 'CP (Continental breakfast)',
    EP: 'EP (Room only)',
    AI: 'AI (All inclusive)',
  };
  return map[v] ?? v;
}

/**
 * Reconstruct the lead's stage at a given moment from the stage-history
 * timeline. Used by the Follow-Up history table's "Lead Status After
 * Follow-Up" column (legacy parity - the legacy schema stamped the stage
 * onto the follow-up row itself; ours derives it post hoc).
 *
 * `stageHistory` arrives sorted descending by `changedAt` (per the API's
 * `leadsService.detailInclude`), so the first entry whose `changedAt` is
 * <= the target time is the active stage at that moment. When the
 * follow-up predates every history entry (rare - usually means migrated
 * legacy data), fall back to the oldest entry's `fromStatus`. Returns a
 * `Status` or `Status(SubStatus)` string for legacy-parity display.
 */
function leadStageAtTime(
  stageHistory: ReadonlyArray<LeadStageHistoryEntry>,
  atIso: string,
): string {
  for (const h of stageHistory) {
    if (h.changedAt <= atIso) {
      const status = h.toStatus.name;
      const sub = h.toSubStatus?.name;
      return sub ? `${status}(${sub})` : status;
    }
  }
  const oldest = stageHistory[stageHistory.length - 1];
  if (oldest) {
    const status = oldest.fromStatus?.name ?? oldest.toStatus.name;
    const sub = oldest.fromSubStatus?.name ?? oldest.toSubStatus?.name;
    return sub ? `${status}(${sub})` : status;
  }
  return '-';
}

function humanResult(r: LeadFollowUp['result']): string {
  return r.charAt(0) + r.slice(1).toLowerCase().replace('_', ' ');
}

/**
 * Split a customer's display name into first / last for the Customer details
 * card. The customer master doesn't return first/last on the lead-detail
 * customer ref (display name only), so we derive a best-guess from whitespace.
 * A future enhancement can include them explicitly when the lead schema
 * widens the customer ref.
 */
function parseDisplayName(name: string): { first: string | null; last: string | null } {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return { first: null, last: null };
  if (parts.length === 1) return { first: parts[0] ?? null, last: null };
  return { first: parts[0] ?? null, last: parts.slice(1).join(' ') };
}

/** Pull the destination half out of the synthesised title `"<from> → <to>"`. */
function extractDestination(title: string): string | null {
  const idx = title.indexOf('→');
  if (idx >= 0) return title.slice(idx + 1).trim();
  return null;
}

function extractDeparture(title: string): string | null {
  const idx = title.indexOf('→');
  if (idx > 0) return title.slice(0, idx).trim();
  return null;
}

function computeNights(start: string | null, end: string | null): number | null {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  return Math.round(ms / 86_400_000);
}

/** Domestic vs International - best-effort heuristic on the title (legacy
 *  `tbl_CRMLead.IsInternationalQuery` doesn't have a clean new equivalent
 *  yet; the typed flag is deferred to a follow-up schema change). */
function inferCategory(title: string): string | null {
  if (!title) return null;
  const intl =
    /\b(dubai|maldives|thailand|bali|singapore|vietnam|europe|mauritius|paris|london|new york|tokyo)\b/i;
  return intl.test(title) ? 'International' : 'Domestic';
}
